import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import './LoanReport.css';

import CustomToolbar from '../global/CustomToolbar';
import withShipment from '../../withShipment';
import { userInfoSelector } from '../../redux/selectors/auth';
import {
    borrowersSelector,
    totalOutstandingSelector,
    activeLoansSelector,
} from '../../redux/selectors/loan';
import {
    listCustomerLoan,
    createBorrower,
    editBorrower,
    addTransaction,
    editTransaction,
    deleteTransaction,
    deleteBorrower,
} from '../../redux/actions/loan';

/* ===========================================================================
   BeeNexa · Loans Module · v1.0
   ---------------------------------------------------------------------------
   Implements the spec exactly:
     • Two-screen architecture (List + Detail) controlled by a single `view`
       state variable. PDF preview is a full-screen overlay (`showPdf`).
     • Inline styles only — no MUI / no external UI library.
     • Sticky header + summary card + search bar; only the borrower list
       scrolls. Achieved with a flex column wrapper + overflow-y on the list.
     • Sample data is loaded from the BeeNexa API on mount via the redux
       saga (`listCustomerLoan`). Production-ready: every form action calls
       a real API endpoint and optimistically updates redux state.
   =========================================================================== */

// ---------------------------------------------------------------------------
//  Color system & typography (per spec section 05)
// ---------------------------------------------------------------------------

const C = {
    headerGreen: '#6B8F7B',
    primaryGreen: '#2D5A3D',
    navyDark: '#1E293B',
    paymentGreen: '#4CAF50',
    loanRed: '#E57373',
    deleteRed: '#C62828',
    bg: '#FAFAFA',
    border: '#E5E7EB',
    muted: '#64748B',
    white: '#FFFFFF',
    // Interest module palette (spec section 8 — color system)
    fixedPrimary: '#7C3AED',
    fixedBg: '#F3E8FF',
    pctPrimary: '#0891B2',
    pctBg: '#CFFAFE',
    warning: '#FF9800',
    warningDark: '#E65100',
    warningBg: '#FFF3E0',
    internalPanel: '#1E293B',
};

const FONT = `'DM Sans', system-ui, sans-serif`;

// ---------------------------------------------------------------------------
//  Tiny utilities
// ---------------------------------------------------------------------------

const fmtMoney = (n, withCents = false) => {
    const v = Number(n) || 0;
    return v.toLocaleString('en-US', {
        minimumFractionDigits: withCents ? 2 : 0,
        maximumFractionDigits: withCents ? 2 : 0,
    });
};

const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.replace(/-/g, '/'));
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const todayISO = () => new Date().toISOString().split('T')[0];

// Add `n` whole months to an ISO date (yyyy-mm-dd). Clamps the day to the
// last day of the resulting month so e.g. Jan 31 + 1 month → Feb 28/29.
const addMonthsISO = (iso, n) => {
    const months = Number(n) || 0;
    if (!iso || !months) return iso || '';
    const parts = iso.split('-');
    if (parts.length !== 3) return iso;
    const yr = Number(parts[0]);
    const mo = Number(parts[1]) - 1;
    const da = Number(parts[2]);
    const target = new Date(yr, mo + months, 1);
    const lastDay = new Date(
        target.getFullYear(),
        target.getMonth() + 1,
        0,
    ).getDate();
    target.setDate(Math.min(da, lastDay));
    const ty = target.getFullYear();
    const tm = String(target.getMonth() + 1).padStart(2, '0');
    const td = String(target.getDate()).padStart(2, '0');
    return `${ty}-${tm}-${td}`;
};

const sumBy = (arr, pred) =>
    arr.reduce(
        (acc, t) => (pred(t) ? acc + (Number(t.amount) || 0) : acc),
        0,
    );

// ---------------------------------------------------------------------------
//  Interest math (spec sections 03, 04, 05, 06)
// ---------------------------------------------------------------------------

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Effective monthly rate for percentage loans; converts annual → monthly.
const effectiveMonthlyRate = (rate, period) => {
    const r = (Number(rate) || 0) / 100;
    return period === 'annual' ? r / 12 : r;
};

// Capital portion required per month for a fixed-interest loan.
const capitalPerMonth = (loan) => {
    const principal = Number(loan.totalLoaned) || 0;
    const term = Number(loan.termMonths) || 0;
    if (term <= 0) return 0;
    return round2(principal / term);
};

// What interest is owed on the NEXT scheduled payment.
//   fixed       → flat monthlyInterest
//   percentage  → balance × effective monthly rate
//   none        → 0
const calcInterestDue = (loan) => {
    const interestType = loan.interestType || 'none';
    if (interestType === 'fixed') {
        return round2(Number(loan.monthlyInterest) || 0);
    }
    if (interestType === 'percentage') {
        const balance = Math.max(0, Number(loan.balance) || 0);
        const r = effectiveMonthlyRate(loan.interestRate, loan.rateType);
        return round2(balance * r);
    }
    return 0;
};

// Standard split: interest first, then principal.
const splitPayment = (payment, intDue) => {
    const p = Math.max(0, Number(payment) || 0);
    const i = Math.max(0, Number(intDue) || 0);
    const toInterest = Math.min(p, i);
    const toPrincipal = Math.max(0, p - i);
    return { toInterest: round2(toInterest), toPrincipal: round2(toPrincipal) };
};

// Standard amortization for a percentage loan.
//   payment = (P × r × (1+r)^n) / ((1+r)^n − 1)
const calcAmortizationPayment = (principal, months, rate, period) => {
    const P = Number(principal) || 0;
    const n = Number(months) || 0;
    const r = effectiveMonthlyRate(rate, period);
    if (P <= 0 || n <= 0) return 0;
    if (r === 0) return round2(P / n);
    const factor = Math.pow(1 + r, n);
    return round2((P * r * factor) / (factor - 1));
};

// Build a full amortization schedule (used by the calculator + quote PDF).
//   `type` is "fixed" or "percentage".
const buildAmortization = ({ principal, months, type, rate, period, monthlyInterest }) => {
    const P = Number(principal) || 0;
    const n = Number(months) || 0;
    if (P <= 0 || n <= 0) return [];

    const rows = [];
    if (type === 'fixed') {
        const cap = round2(P / n);
        const fixedInt = Number(monthlyInterest) || 0;
        let bal = P;
        for (let i = 1; i <= n; i += 1) {
            const principalPart = i === n ? round2(bal) : cap;
            bal = round2(bal - principalPart);
            rows.push({
                month: i,
                payment: round2(principalPart + fixedInt),
                interest: round2(fixedInt),
                principal: principalPart,
                balance: Math.max(0, bal),
            });
        }
        return rows;
    }
    // percentage
    const r = effectiveMonthlyRate(rate, period);
    const payment = calcAmortizationPayment(P, n, rate, period);
    let bal = P;
    for (let i = 1; i <= n; i += 1) {
        const interestPart = round2(bal * r);
        let principalPart = round2(payment - interestPart);
        // Last row: snap any rounding drift to clear the balance.
        if (i === n) principalPart = round2(bal);
        bal = round2(bal - principalPart);
        rows.push({
            month: i,
            payment: round2(principalPart + interestPart),
            interest: interestPart,
            principal: principalPart,
            balance: Math.max(0, bal),
        });
    }
    return rows;
};

// ---------------------------------------------------------------------------
//  Inline icon components — kept dependency-free
// ---------------------------------------------------------------------------

const Icon = ({ d, size = 20, color = 'currentColor' }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d={d} />
    </svg>
);

const IconBack = (p) => <Icon {...p} d="M19 12H5 M12 19l-7-7 7-7" />;
const IconSearch = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
    </svg>
);
const IconSort = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18 M6 12h12 M10 18h4" />
    </svg>
);
const IconClose = (p) => <Icon {...p} d="M18 6L6 18 M6 6l12 12" />;
const IconPlus = (p) => <Icon {...p} d="M12 5v14 M5 12h14" />;
const IconDollar = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v20" />
        <path d="M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
);
const IconDownload = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="M7 10l5 5 5-5" />
        <path d="M12 15V3" />
    </svg>
);
const IconTrash = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 6h18 M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2 M10 11v6 M14 11v6" />
        <path d="M5 6l1 14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-14" />
    </svg>
);
const IconEdit = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);
const IconCalculator = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" />
        <line x1="8" y1="6" x2="16" y2="6" />
        <line x1="8" y1="10" x2="8.01" y2="10" />
        <line x1="12" y1="10" x2="12.01" y2="10" />
        <line x1="16" y1="10" x2="16.01" y2="10" />
        <line x1="8" y1="14" x2="8.01" y2="14" />
        <line x1="12" y1="14" x2="12.01" y2="14" />
        <line x1="16" y1="14" x2="16.01" y2="14" />
        <line x1="8" y1="18" x2="12" y2="18" />
        <line x1="16" y1="18" x2="16.01" y2="18" />
    </svg>
);
const IconWarning = (p) => (
    <svg width={p.size || 18} height={p.size || 18} viewBox="0 0 24 24" fill="none"
         stroke={p.color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);
// Pill badge that surfaces the loan's interest config in the list + detail.
const InterestBadge = ({ loan, size = 'sm' }) => {
    const t = loan.interestType || 'none';
    if (t === 'none') return null;
    const isFixed = t === 'fixed';
    const bg = isFixed ? C.fixedBg : C.pctBg;
    const fg = isFixed ? C.fixedPrimary : C.pctPrimary;
    const label = isFixed
        ? `+ $${fmtMoney(loan.monthlyInterest, true)}/mo`
        : `${fmtMoney(loan.interestRate, true)}% ${
              loan.rateType === 'annual' ? 'yr' : 'mo'
          }`;
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: bg,
                color: fg,
                padding: size === 'lg' ? '6px 12px' : '3px 9px',
                borderRadius: 999,
                fontSize: size === 'lg' ? 13 : 11,
                fontWeight: 700,
                fontFamily: FONT,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </span>
    );
};

// Bilingual high-risk warning shown when monthly % is selected (spec 04).
const HighRiskWarning = () => (
    <div
        style={{
            background: C.warningBg,
            border: `2px solid ${C.warning}`,
            borderRadius: 12,
            padding: '12px 14px',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            marginTop: 12,
        }}
    >
        <span style={{ color: C.warningDark, flexShrink: 0, marginTop: 2 }}>
            <IconWarning size={20} />
        </span>
        <div style={{ fontSize: 12.5, color: C.warningDark, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Préstamo de alto riesgo.
            </div>
            <div style={{ marginBottom: 6 }}>
                Las tasas mensuales se usan típicamente para préstamos a corto
                plazo o sin garantía.
            </div>
            <div
                style={{
                    fontSize: 11.5,
                    fontStyle: 'italic',
                    opacity: 0.85,
                }}
            >
                High-risk loan. Monthly rates are typically used for short-term
                or unsecured lending.
            </div>
        </div>
    </div>
);

// Internal-only tier reference — NEVER rendered into PDFs (spec 04).
const TierPanel = () => (
    <div
        style={{
            background: C.internalPanel,
            color: C.white,
            borderRadius: 12,
            padding: '14px 16px',
            marginTop: 10,
            fontFamily: FONT,
        }}
    >
        <div
            style={{
                fontSize: 10,
                letterSpacing: 1.5,
                opacity: 0.65,
                textTransform: 'uppercase',
                fontWeight: 700,
                marginBottom: 6,
            }}
        >
            Internal · Tier Reference
        </div>
        {[
            { tier: 'Tier 1', sub: 'confiables', range: '2% – 3% mensual' },
            { tier: 'Tier 2', sub: 'normal', range: '3% – 4% mensual' },
            { tier: 'Tier 3', sub: 'alto riesgo', range: '5% – 6% mensual' },
        ].map((row) => (
            <div
                key={row.tier}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12.5,
                    padding: '4px 0',
                }}
            >
                <span>
                    <strong>{row.tier}</strong>{' '}
                    <span style={{ opacity: 0.65 }}>({row.sub})</span>
                </span>
                <span style={{ fontWeight: 600 }}>{row.range}</span>
            </div>
        ))}
        <div
            style={{
                fontSize: 10.5,
                opacity: 0.55,
                marginTop: 8,
                fontStyle: 'italic',
            }}
        >
            For internal reference only. Never appears in client PDFs or
            quotes.
        </div>
    </div>
);

// ===========================================================================
//  Reusable Field component (spec section 04)
// ===========================================================================

// ---------------------------------------------------------------------------
//  DatePicker — custom calendar popup, no native browser styling.
// ---------------------------------------------------------------------------

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const parseISO = (iso) => {
    if (!iso) return null;
    const parts = iso.split('-');
    if (parts.length !== 3) return null;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return Number.isNaN(d.getTime()) ? null : d;
};

const toISO = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
};

const sameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

// Years are paginated 12 at a time in the year-picker view. Returns the
// inclusive [start, end] range that contains `year`.
const yearPage = (year) => {
    const start = year - ((year % 12) + 12) % 12;
    return [start, start + 11];
};

class DatePicker extends React.Component {
    constructor(props) {
        super(props);
        const initial = parseISO(props.value) || new Date();
        this.state = {
            open: false,
            // 'days' = day grid; 'months' = month grid w/ year nav, so the
            // user can jump month+year in two clicks instead of mashing
            // the chevron.
            view: 'days',
            // The month currently being shown in the calendar (1st of month).
            cursor: new Date(initial.getFullYear(), initial.getMonth(), 1),
        };
        this.rootRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (e) => {
        if (
            this.state.open &&
            this.rootRef.current &&
            !this.rootRef.current.contains(e.target)
        ) {
            this.setState({ open: false });
        }
    };

    openPicker = () => {
        const initial = parseISO(this.props.value) || new Date();
        this.setState({
            open: true,
            view: 'days',
            cursor: new Date(initial.getFullYear(), initial.getMonth(), 1),
        });
    };

    prevMonth = () =>
        this.setState((s) => ({
            cursor: new Date(s.cursor.getFullYear(), s.cursor.getMonth() - 1, 1),
        }));

    nextMonth = () =>
        this.setState((s) => ({
            cursor: new Date(s.cursor.getFullYear(), s.cursor.getMonth() + 1, 1),
        }));

    prevYear = () =>
        this.setState((s) => ({
            cursor: new Date(s.cursor.getFullYear() - 1, s.cursor.getMonth(), 1),
        }));

    nextYear = () =>
        this.setState((s) => ({
            cursor: new Date(s.cursor.getFullYear() + 1, s.cursor.getMonth(), 1),
        }));

    prevYearPage = () =>
        this.setState((s) => ({
            cursor: new Date(s.cursor.getFullYear() - 12, s.cursor.getMonth(), 1),
        }));

    nextYearPage = () =>
        this.setState((s) => ({
            cursor: new Date(s.cursor.getFullYear() + 12, s.cursor.getMonth(), 1),
        }));

    cycleView = () =>
        this.setState((s) => ({
            view: s.view === 'days' ? 'months' : s.view === 'months' ? 'years' : 'days',
        }));

    selectMonth = (monthIdx) =>
        this.setState((s) => ({
            view: 'days',
            cursor: new Date(s.cursor.getFullYear(), monthIdx, 1),
        }));

    selectYear = (yr) =>
        this.setState((s) => ({
            view: 'months',
            cursor: new Date(yr, s.cursor.getMonth(), 1),
        }));

    selectDay = (day) => {
        const { cursor } = this.state;
        const d = new Date(cursor.getFullYear(), cursor.getMonth(), day);
        this.props.onChange(toISO(d));
        this.setState({ open: false });
    };

    selectToday = () => {
        const today = new Date();
        this.props.onChange(toISO(today));
        this.setState({
            open: false,
            view: 'days',
            cursor: new Date(today.getFullYear(), today.getMonth(), 1),
        });
    };

    render() {
        const { value, placeholder } = this.props;
        const { open, cursor, view } = this.state;

        const selected = parseISO(value);
        const today = new Date();

        // Build the calendar grid: leading blanks + days of month.
        const firstWeekday = new Date(
            cursor.getFullYear(),
            cursor.getMonth(),
            1,
        ).getDay();
        const daysInMonth = new Date(
            cursor.getFullYear(),
            cursor.getMonth() + 1,
            0,
        ).getDate();

        const cells = [];
        for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
        for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
        while (cells.length % 7 !== 0) cells.push(null);

        const displayValue = selected
            ? selected.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
              })
            : '';

        return (
            <div ref={this.rootRef} style={{ position: 'relative' }}>
                <button
                    type="button"
                    onClick={() =>
                        this.setState((s) => ({ open: !s.open }), () => {
                            if (this.state.open) this.openPicker();
                        })
                    }
                    style={{
                        width: '100%',
                        padding: '12px 14px',
                        fontSize: 15,
                        fontFamily: FONT,
                        textAlign: 'left',
                        border: `2px solid ${open ? C.primaryGreen : C.border}`,
                        borderRadius: 12,
                        background: C.white,
                        color: displayValue ? C.navyDark : C.muted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'border-color 0.15s ease',
                    }}
                >
                    <span>{displayValue || placeholder || 'Select date'}</span>
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={C.muted}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4 M8 2v4 M3 10h18" />
                    </svg>
                </button>

                {open && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            right: 0,
                            background: C.white,
                            borderRadius: 16,
                            boxShadow: '0 16px 40px rgba(15, 23, 42, 0.22)',
                            border: `1px solid ${C.border}`,
                            padding: 16,
                            zIndex: 9100,
                            animation: 'bnPopIn 0.18s ease',
                            fontFamily: FONT,
                        }}
                    >
                        {/* Header — prev/next + clickable label that drills
                            from days → months → years. */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 12,
                            }}
                        >
                            <button
                                type="button"
                                onClick={view === 'days' ? this.prevMonth : view === 'months' ? this.prevYear : this.prevYearPage}
                                aria-label="Previous"
                                style={{
                                    background: '#F1F5F9',
                                    border: 'none',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    color: C.navyDark,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor"
                                     strokeWidth="2.5" strokeLinecap="round"
                                     strokeLinejoin="round">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={this.cycleView}
                                aria-label="Switch view"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: C.navyDark,
                                    fontFamily: FONT,
                                    cursor: 'pointer',
                                    padding: '4px 10px',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                {view === 'days' && `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
                                {view === 'months' && cursor.getFullYear()}
                                {view === 'years' && (() => { const [s, e] = yearPage(cursor.getFullYear()); return `${s} – ${e}`; })()}
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d={view === 'years' ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={view === 'days' ? this.nextMonth : view === 'months' ? this.nextYear : this.nextYearPage}
                                aria-label="Next"
                                style={{
                                    background: '#F1F5F9',
                                    border: 'none',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    color: C.navyDark,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor"
                                     strokeWidth="2.5" strokeLinecap="round"
                                     strokeLinejoin="round">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        </div>

                        {view === 'days' ? (
                            <>
                                {/* Weekday header */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, 1fr)',
                                        gap: 4,
                                        marginBottom: 6,
                                    }}
                                >
                                    {WEEKDAYS.map((w, i) => (
                                        <div
                                            key={`${w}-${i}`}
                                            style={{
                                                textAlign: 'center',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                color: C.muted,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.5,
                                                padding: '4px 0',
                                            }}
                                        >
                                            {w}
                                        </div>
                                    ))}
                                </div>

                                {/* Day grid */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(7, 1fr)',
                                        gap: 4,
                                    }}
                                >
                                    {cells.map((day, idx) => {
                                        if (day === null) {
                                            return <div key={`b-${idx}`} />;
                                        }
                                        const cellDate = new Date(
                                            cursor.getFullYear(),
                                            cursor.getMonth(),
                                            day,
                                        );
                                        const isSelected = sameDay(cellDate, selected);
                                        const isToday = sameDay(cellDate, today);

                                        let bg = 'transparent';
                                        let color = C.navyDark;
                                        let fontWeight = 500;
                                        let border = '1px solid transparent';

                                        if (isSelected) {
                                            bg = C.primaryGreen;
                                            color = C.white;
                                            fontWeight = 700;
                                        } else if (isToday) {
                                            border = `1px solid ${C.primaryGreen}`;
                                            color = C.primaryGreen;
                                            fontWeight = 700;
                                        }

                                        return (
                                            <button
                                                key={day}
                                                type="button"
                                                onClick={() => this.selectDay(day)}
                                                style={{
                                                    background: bg,
                                                    color,
                                                    fontWeight,
                                                    border,
                                                    borderRadius: 10,
                                                    height: 36,
                                                    cursor: 'pointer',
                                                    fontFamily: FONT,
                                                    fontSize: 14,
                                                    transition:
                                                        'background 0.12s, color 0.12s',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.background =
                                                            '#F0FDF4';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.background =
                                                            'transparent';
                                                    }
                                                }}
                                            >
                                                {day}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        ) : view === 'months' ? (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 6,
                                }}
                            >
                                {MONTHS_SHORT.map((m, idx) => {
                                    const isCurrent = idx === cursor.getMonth();
                                    const isThisMonth =
                                        today.getFullYear() === cursor.getFullYear() &&
                                        today.getMonth() === idx;
                                    let bg = 'transparent';
                                    let color = C.navyDark;
                                    let fontWeight = 500;
                                    let border = '1px solid transparent';
                                    if (isCurrent) {
                                        bg = C.primaryGreen;
                                        color = C.white;
                                        fontWeight = 700;
                                    } else if (isThisMonth) {
                                        border = `1px solid ${C.primaryGreen}`;
                                        color = C.primaryGreen;
                                        fontWeight = 700;
                                    }
                                    return (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => this.selectMonth(idx)}
                                            style={{
                                                background: bg,
                                                color,
                                                fontWeight,
                                                border,
                                                borderRadius: 10,
                                                height: 48,
                                                cursor: 'pointer',
                                                fontFamily: FONT,
                                                fontSize: 14,
                                                transition:
                                                    'background 0.12s, color 0.12s',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isCurrent) {
                                                    e.currentTarget.style.background = '#F0FDF4';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isCurrent) {
                                                    e.currentTarget.style.background = 'transparent';
                                                }
                                            }}
                                        >
                                            {m}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: 6,
                                }}
                            >
                                {(() => {
                                    const [startYr] = yearPage(cursor.getFullYear());
                                    const years = [];
                                    for (let i = 0; i < 12; i += 1) years.push(startYr + i);
                                    return years.map((yr) => {
                                        const isCurrent = yr === cursor.getFullYear();
                                        const isThisYear = yr === today.getFullYear();
                                        let bg = 'transparent';
                                        let color = C.navyDark;
                                        let fontWeight = 500;
                                        let border = '1px solid transparent';
                                        if (isCurrent) {
                                            bg = C.primaryGreen;
                                            color = C.white;
                                            fontWeight = 700;
                                        } else if (isThisYear) {
                                            border = `1px solid ${C.primaryGreen}`;
                                            color = C.primaryGreen;
                                            fontWeight = 700;
                                        }
                                        return (
                                            <button
                                                key={yr}
                                                type="button"
                                                onClick={() => this.selectYear(yr)}
                                                style={{
                                                    background: bg,
                                                    color,
                                                    fontWeight,
                                                    border,
                                                    borderRadius: 10,
                                                    height: 48,
                                                    cursor: 'pointer',
                                                    fontFamily: FONT,
                                                    fontSize: 14,
                                                    transition:
                                                        'background 0.12s, color 0.12s',
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isCurrent) {
                                                        e.currentTarget.style.background = '#F0FDF4';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isCurrent) {
                                                        e.currentTarget.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                {yr}
                                            </button>
                                        );
                                    });
                                })()}
                            </div>
                        )}

                        {/* Footer: Today shortcut */}
                        <div
                            style={{
                                marginTop: 12,
                                paddingTop: 12,
                                borderTop: `1px solid ${C.border}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <button
                                type="button"
                                onClick={this.selectToday}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: C.primaryGreen,
                                    fontFamily: FONT,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    padding: '6px 8px',
                                }}
                            >
                                Today
                            </button>
                            <button
                                type="button"
                                onClick={() => this.setState({ open: false })}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: C.muted,
                                    fontFamily: FONT,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    padding: '6px 8px',
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

class Field extends React.Component {
    state = { focused: false };
    render() {
        const {
            label,
            value,
            onChange,
            type = 'text',
            prefix,
            multiline,
            placeholder,
        } = this.props;

        // Date fields use the custom calendar popup instead of <input type="date">.
        if (type === 'date') {
            return (
                <div style={{ marginBottom: 16 }}>
                    {label && (
                        <label
                            style={{
                                display: 'block',
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                marginBottom: 6,
                            }}
                        >
                            {label}
                        </label>
                    )}
                    <DatePicker
                        value={value}
                        onChange={onChange}
                        placeholder={placeholder}
                    />
                </div>
            );
        }

        const baseStyle = {
            width: '100%',
            padding: prefix ? '12px 14px 12px 32px' : '12px 14px',
            fontSize: 15,
            fontFamily: FONT,
            border: `2px solid ${this.state.focused ? C.primaryGreen : C.border}`,
            borderRadius: 12,
            outline: 'none',
            background: C.white,
            color: C.navyDark,
            transition: 'border-color 0.15s ease',
            resize: multiline ? 'vertical' : 'none',
        };

        return (
            <div style={{ marginBottom: 16 }}>
                {label && (
                    <label
                        style={{
                            display: 'block',
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 1,
                            marginBottom: 6,
                        }}
                    >
                        {label}
                    </label>
                )}
                <div style={{ position: 'relative' }}>
                    {prefix && (
                        <span
                            style={{
                                position: 'absolute',
                                left: 14,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: C.muted,
                                fontWeight: 600,
                                fontSize: 15,
                                pointerEvents: 'none',
                            }}
                        >
                            {prefix}
                        </span>
                    )}
                    {multiline ? (
                        <textarea
                            rows={3}
                            value={value || ''}
                            placeholder={placeholder}
                            onChange={(e) => onChange(e.target.value)}
                            onFocus={() => this.setState({ focused: true })}
                            onBlur={() => this.setState({ focused: false })}
                            style={baseStyle}
                        />
                    ) : (
                        <input
                            type={type}
                            value={value || ''}
                            placeholder={placeholder}
                            onChange={(e) => onChange(e.target.value)}
                            onFocus={() => this.setState({ focused: true })}
                            onBlur={() => this.setState({ focused: false })}
                            style={baseStyle}
                        />
                    )}
                </div>
            </div>
        );
    }
}

// ===========================================================================
//  BottomSheet (spec section 04)
// ===========================================================================

const BottomSheet = ({ open, onClose, title, children }) => {
    if (!open) return null;
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15, 23, 42, 0.55)',
                zIndex: 9000,
                animation: 'bnFadeIn 0.2s ease',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bnxa-modal-panel"
                style={{
                    width: '100%',
                    maxWidth: 520,
                    background: C.white,
                    borderTopLeftRadius: 24,
                    borderTopRightRadius: 24,
                    padding: '12px 24px 28px',
                    animation: 'bnSlideUp 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
                    fontFamily: FONT,
                    boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.15)',
                }}
            >
                {/* Drag handle */}
                <div
                    style={{
                        width: 40,
                        height: 4,
                        background: '#D1D5DB',
                        borderRadius: 999,
                        margin: '0 auto 16px',
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 20,
                    }}
                >
                    <h3
                        style={{
                            margin: 0,
                            fontSize: 20,
                            fontWeight: 700,
                            color: C.navyDark,
                        }}
                    >
                        {title}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 8,
                            borderRadius: 999,
                            color: C.muted,
                        }}
                    >
                        <IconClose />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
};

// ===========================================================================
//  Action Pill (spec section 04)
// ===========================================================================

const ActionPill = ({ icon, label, color, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            background: color,
            color: C.white,
            border: 'none',
            padding: '9px 14px',
            borderRadius: 999,
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: FONT,
            cursor: 'pointer',
            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)',
            transition: 'transform 0.12s ease',
            whiteSpace: 'nowrap',
            flex: '0 1 auto',
            minWidth: 0,
        }}
        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
    >
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
            {React.cloneElement(icon, { size: 15 })}
        </span>
        {label}
    </button>
);

// ===========================================================================
//  Submit button used inside bottom sheets
// ===========================================================================

const SubmitBtn = ({ label, color, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            width: '100%',
            background: color,
            color: C.white,
            border: 'none',
            padding: '14px 20px',
            borderRadius: 12,
            fontSize: 16,
            fontWeight: 700,
            fontFamily: FONT,
            cursor: 'pointer',
            marginTop: 8,
            boxShadow: `0 8px 24px ${color}33`,
        }}
    >
        {label}
    </button>
);

// ===========================================================================
//  TxRow (spec section 04)
// ===========================================================================

const TxRow = ({ tx, onEdit, onDelete }) => {
    const isPayment = tx.type === 'payment';
    const dotColor = isPayment ? C.paymentGreen : C.loanRed;
    const sign = isPayment ? '−' : '+';
    const amountColor = isPayment ? C.paymentGreen : C.loanRed;
    // Show split pills only when the row carries an actual interest portion.
    const hasSplit =
        isPayment &&
        Number(tx.toInterest) > 0 &&
        Number(tx.toPrincipal) >= 0;

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
                padding: '14px 0',
                borderBottom: `1px solid ${C.border}`,
            }}
        >
            <div
                style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: dotColor,
                    marginTop: 6,
                    flexShrink: 0,
                }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: C.navyDark,
                                textTransform: 'capitalize',
                            }}
                        >
                            {tx.type}
                        </div>
                        <div
                            style={{
                                fontSize: 12,
                                color: C.muted,
                                marginTop: 2,
                            }}
                        >
                            {fmtDate(tx.date)}
                        </div>
                        {hasSplit && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 6,
                                    marginTop: 6,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span
                                    style={{
                                        background: C.fixedBg,
                                        color: C.fixedPrimary,
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}
                                >
                                    Int: ${fmtMoney(tx.toInterest, true)}
                                </span>
                                <span
                                    style={{
                                        background: '#E8F5E9',
                                        color: C.primaryGreen,
                                        padding: '2px 8px',
                                        borderRadius: 999,
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}
                                >
                                    Cap: ${fmtMoney(tx.toPrincipal, true)}
                                </span>
                            </div>
                        )}
                        {tx.note ? (
                            <div
                                style={{
                                    fontSize: 12,
                                    color: C.muted,
                                    marginTop: 4,
                                    lineHeight: 1.45,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {tx.note}
                            </div>
                        ) : null}
                    </div>
                    <div
                        style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: amountColor,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {sign} ${fmtMoney(tx.amount, true)}
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                {onEdit && (
                    <button
                        type="button"
                        onClick={onEdit}
                        title="Edit transaction"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            color: C.muted,
                            opacity: 0.75,
                        }}
                    >
                        <IconEdit size={16} />
                    </button>
                )}
                {onDelete && (
                    <button
                        type="button"
                        onClick={onDelete}
                        title="Delete transaction"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 4,
                            color: C.deleteRed,
                            opacity: 0.75,
                        }}
                    >
                        <IconTrash size={16} />
                    </button>
                )}
            </div>
        </div>
    );
};

// ===========================================================================
//  PersonCard (spec section 04)
// ===========================================================================

const PersonCard = ({ borrower, onClick }) => {
    const balance = Number(borrower.balance) || 0;
    const txCount = (borrower.transactions || []).length;
    const balanceColor = balance >= 0 ? C.primaryGreen : C.deleteRed;

    return (
        <div
            onClick={onClick}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '18px 20px',
                background: C.white,
                borderRadius: 14,
                marginBottom: 10,
                borderLeft: `4px solid ${C.primaryGreen}`,
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                cursor: 'pointer',
                transition: 'transform 0.12s ease, box-shadow 0.12s ease',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.08)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(15, 23, 42, 0.06)';
            }}
        >
            <div style={{ minWidth: 0, flex: 1, paddingRight: 12 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    <div
                        style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: C.navyDark,
                        }}
                    >
                        {borrower.name}
                    </div>
                    <InterestBadge loan={borrower} />
                </div>
                <div
                    style={{
                        fontSize: 12,
                        color: C.muted,
                        marginTop: 2,
                    }}
                >
                    {txCount} transaction{txCount === 1 ? '' : 's'}
                </div>
            </div>
            <div
                style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: balanceColor,
                }}
            >
                ${fmtMoney(balance, true)}
            </div>
        </div>
    );
};

// ===========================================================================
//  Snackbar (spec section 04 — replaces the original Toast)
// ===========================================================================

const SNACKBAR_DURATION_MS = 3000;

const SnackbarIconCheck = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
    </svg>
);

const SnackbarIconAlert = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4 M12 16h.01" />
    </svg>
);

const Snackbar = ({ message, severity, onClose }) => {
    if (!message) return null;

    const isError = severity === 'error';
    const accent = isError ? C.deleteRed : C.primaryGreen;
    const tint = isError ? '#FEE2E2' : '#E8F5E9';
    const Icon = isError ? SnackbarIconAlert : SnackbarIconCheck;

    return (
        <div
            className="bn-no-print"
            role="status"
            aria-live="polite"
            style={{
                position: 'fixed',
                bottom: 110,
                left: '50%',
                transform: 'translateX(-50%)',
                background: C.white,
                color: C.navyDark,
                minWidth: 280,
                maxWidth: '90vw',
                borderRadius: 14,
                fontFamily: FONT,
                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.22)',
                zIndex: 9500,
                animation: 'bnSnackbarIn 0.28s cubic-bezier(0.16, 1, 0.3, 1)',
                overflow: 'hidden',
                borderLeft: `4px solid ${accent}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 14px 14px 16px',
                }}
            >
                <div
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: tint,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Icon color={accent} />
                </div>
                <div
                    style={{
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 600,
                        lineHeight: 1.4,
                    }}
                >
                    {message}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Dismiss notification"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 6,
                        color: C.muted,
                        borderRadius: 6,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <IconClose size={16} />
                </button>
            </div>
            {/* auto-dismiss progress bar */}
            <div
                style={{
                    height: 3,
                    background: tint,
                    transformOrigin: 'left center',
                    animation: `bnSnackbarProgress ${SNACKBAR_DURATION_MS}ms linear forwards`,
                }}
            />
        </div>
    );
};

// ===========================================================================
//  Main component
// ===========================================================================

class LoanReport extends React.Component {
    state = {
        view: 'list', // "list" | "detail"
        selectedLoanId: null,

        search: '',
        sortMode: 'BALANCE_DESC', // or 'NAME_ASC'
        sortMenuOpen: false,

        // Borrower type tab: "personal" | "investor"
        activeTab: 'personal',

        // Interest filter: "all" | "with" | "without" (spec section 8 — list view filter tabs)
        interestFilter: 'all',

        // Bottom sheets
        sheet: null, // "payment" | "loan" | "new" | "calculator"

        // Form fields (shared between sheets)
        formDate: todayISO(),
        formAmount: '',
        formNote: '',
        formName: '',
        formType: 'personal',
        editingEntryId: null,

        // Interest config inputs (new loan + edit borrower)
        formInterestType: 'none', // "none" | "fixed" | "percentage"
        formMonthlyInterest: '',
        formInterestRate: '',
        formRatePeriod: 'monthly', // "monthly" | "annual"
        formTermMonths: '',
        formMinimumPayment: '',

        // Payment override mode: "auto" splits int/cap from accrued interest;
        // "abono" forces 100% to principal (capital-only payment).
        formPaymentMode: 'auto',

        // Calculator inputs (spec section 6)
        calcClient: '',
        calcAmount: '',
        calcMonths: '',
        calcStartDate: todayISO(),
        calcType: 'fixed',
        calcMonthlyInterest: '',
        calcRate: '',
        calcRatePeriod: 'monthly',

        // PDF preview overlay
        showPdf: false,

        // Confirm dialog (delete transaction, etc.)
        confirm: null, // { title, message, confirmLabel, danger, onConfirm }

        // Snackbar
        snackbar: { message: '', severity: 'success' },
    };

    snackbarTimer = null;

    // -----------------------------------------------------------------
    componentDidMount() {
        this.props.listCustomerLoan({
            locationId:
                this.props.userInfo?.company || this.props.userInfo?.companyId,
        });
    }

    componentWillUnmount() {
        if (this.snackbarTimer) clearTimeout(this.snackbarTimer);
    }

    // -----------------------------------------------------------------
    showSnackbar = (message, severity = 'success') => {
        if (this.snackbarTimer) clearTimeout(this.snackbarTimer);
        // Briefly clear so the same message can re-trigger the animation.
        this.setState({ snackbar: { message: '', severity } }, () => {
            this.setState({ snackbar: { message, severity } });
            this.snackbarTimer = setTimeout(
                () =>
                    this.setState({
                        snackbar: { message: '', severity: 'success' },
                    }),
                SNACKBAR_DURATION_MS,
            );
        });
    };

    closeSnackbar = () =>
        this.setState({ snackbar: { message: '', severity: 'success' } });

    // Convenience helpers used throughout the form handlers.
    showSuccess = (msg) => this.showSnackbar(msg, 'success');
    showError = (msg) => this.showSnackbar(msg, 'error');

    // -----------------------------------------------------------------
    getSelectedLoan = () =>
        (this.props.borrowers || []).find(
            (b) =>
                b.borrowerId === this.state.selectedLoanId ||
                b.id === this.state.selectedLoanId,
        );

    getLocationId = () =>
        this.props.userInfo?.company || this.props.userInfo?.companyId;

    getEmployeeName = () =>
        [this.props.userInfo?.firstname, this.props.userInfo?.lastname]
            .filter(Boolean)
            .join(' ');

    // Authority helpers — admins have full control; supervisors can edit
    // but cannot delete (per BeeNexa Loans v1.0 permissions model).
    //
    // The login saga stores either a single `authority` string OR an
    // `authorities` array depending on the entry point, so we normalize
    // both into one Set.
    getAuthorities = () => {
        const info = this.props.userInfo || {};
        const set = new Set();
        if (info.authority) set.add(info.authority);
        const list = info.authorities || [];
        list.forEach((a) => {
            const value = typeof a === 'string' ? a : a?.authority;
            if (value) set.add(value);
        });
        return set;
    };

    isAdmin = () => {
        const auths = this.getAuthorities();
        return auths.has('ROLE_ADMIN') || auths.has('ROLE_MASTER_ADMIN');
    };

    isSupervisor = () => this.getAuthorities().has('ROLE_SUPERVISOR');

    // Edit operations require admin OR supervisor. A user with only
    // ROLE_LOANS gets a read-only view (plus create/add — those are still
    // allowed because the spec lists them as separate actions).
    canEdit = () => this.isAdmin() || this.isSupervisor();

    // -----------------------------------------------------------------
    openSheet = (sheet) =>
        this.setState({
            sheet,
            formDate: todayISO(),
            formAmount: '',
            formNote: '',
            formName: '',
            formType: this.state.activeTab || 'personal',
            editingEntryId: null,
            formInterestType: 'none',
            formMonthlyInterest: '',
            formInterestRate: '',
            formRatePeriod: 'monthly',
            formTermMonths: '',
            formMinimumPayment: '',
            formPaymentMode: 'auto',
        });

    openCalculator = () =>
        this.setState({
            sheet: 'calculator',
            calcClient: '',
            calcAmount: '',
            calcMonths: '',
            calcStartDate: todayISO(),
            calcType: 'fixed',
            calcMonthlyInterest: '',
            calcRate: '',
            calcRatePeriod: 'monthly',
        });

    openEditBorrowerSheet = () => {
        const loan = this.getSelectedLoan();
        if (!loan) return;
        this.setState({
            sheet: 'editBorrower',
            formName: loan.name || '',
            formType: loan.type === 'investor' ? 'investor' : 'personal',
            formDate: loan.startDate || loan.date || todayISO(),
            formAmount: '',
            formNote: '',
            editingEntryId: null,
            formInterestType: loan.interestType || 'none',
            formMonthlyInterest:
                loan.monthlyInterest != null
                    ? String(loan.monthlyInterest)
                    : '',
            formInterestRate:
                loan.interestRate != null ? String(loan.interestRate) : '',
            formRatePeriod: loan.rateType || 'monthly',
            formTermMonths:
                loan.termMonths != null ? String(loan.termMonths) : '',
            formMinimumPayment:
                loan.minimumPayment != null
                    ? String(loan.minimumPayment)
                    : '',
        });
    };

    openEditTransactionSheet = (entry) => {
        this.setState({
            sheet: entry.type === 'payment' ? 'payment' : 'loan',
            editingEntryId: entry.entryId,
            formDate: entry.date || todayISO(),
            formAmount: entry.amount != null ? String(entry.amount) : '',
            formNote: entry.note || '',
            formName: '',
            formPaymentMode: 'auto',
        });
    };

    closeSheet = () =>
        this.setState({ sheet: null, editingEntryId: null });

    // -----------------------------------------------------------------
    submitNewLoan = () => {
        const {
            formName,
            formAmount,
            formDate,
            formNote,
            formType,
            formInterestType,
            formMonthlyInterest,
            formInterestRate,
            formRatePeriod,
            formTermMonths,
            formMinimumPayment,
        } = this.state;
        if (!formName.trim()) return this.showError('Name is required');
        const amt = parseFloat(formAmount);
        if (!amt || amt <= 0) return this.showError('Enter a valid amount');
        const type = formType === 'investor' ? 'investor' : 'personal';

        const interestType = formInterestType || 'none';
        const payload = {
            locationId: this.getLocationId(),
            name: formName.trim(),
            type,
            amount: amt,
            date: formDate,
            note: formNote,
            desc: 'Initial loan',
            employeeName: this.getEmployeeName(),
            interestType,
            termMonths: formTermMonths ? parseInt(formTermMonths, 10) : null,
            minimumPayment: formMinimumPayment
                ? parseFloat(formMinimumPayment)
                : null,
            startDate: formDate,
        };
        if (interestType === 'fixed') {
            const mi = parseFloat(formMonthlyInterest);
            if (!mi || mi <= 0) {
                return this.showError(
                    'Enter the monthly interest charge for a fixed-interest loan',
                );
            }
            if (!payload.termMonths || payload.termMonths <= 0) {
                return this.showError(
                    'Term in months is required for a fixed-interest loan',
                );
            }
            payload.monthlyInterest = mi;
        } else if (interestType === 'percentage') {
            const rate = parseFloat(formInterestRate);
            if (!rate || rate <= 0) {
                return this.showError(
                    'Enter the interest rate for a percentage loan',
                );
            }
            payload.interestRate = rate;
            payload.rateType =
                formRatePeriod === 'annual' ? 'annual' : 'monthly';
        }

        this.props.createBorrower(payload, (err) => {
            if (err) return this.showError(err);
            this.setState({ activeTab: type });
            this.closeSheet();
            this.showSuccess('New loan created');
        });
    };

    submitTransaction = (type) => {
        const loan = this.getSelectedLoan();
        if (!loan) return;
        const {
            formAmount,
            formDate,
            formNote,
            formPaymentMode,
            editingEntryId,
        } = this.state;
        const amt = parseFloat(formAmount);
        if (!amt || amt <= 0) return this.showError('Enter a valid amount');

        // For payments on interest-bearing loans, compute the principal/
        // interest split client-side so the user sees the same numbers in
        // the live preview that the server records.
        let split = null;
        if (type === 'payment' && (loan.interestType || 'none') !== 'none') {
            if (formPaymentMode === 'abono') {
                // Abono → entire payment to principal (override).
                split = { toInterest: 0, toPrincipal: round2(amt) };
            } else {
                const intDue = calcInterestDue(loan);
                split = splitPayment(amt, intDue);
            }
        }

        // Editing an existing transaction
        if (editingEntryId) {
            const editPayload = {
                locationId: this.getLocationId(),
                borrowerId: loan.borrowerId || loan.id,
                entryId: editingEntryId,
                type,
                amount: amt,
                date: formDate,
                note: formNote,
            };
            if (split) {
                editPayload.toInterest = split.toInterest;
                editPayload.toPrincipal = split.toPrincipal;
            }
            this.props.editTransaction(editPayload, (err) => {
                if (err) return this.showError(err);
                this.closeSheet();
                this.showSuccess('Transaction updated');
            });
            return;
        }

        // Creating a new transaction
        const addPayload = {
            locationId: this.getLocationId(),
            borrowerId: loan.borrowerId || loan.id,
            type,
            amount: amt,
            date: formDate,
            note: formNote,
            desc:
                type === 'payment'
                    ? formPaymentMode === 'abono'
                        ? 'Abono (capital only)'
                        : 'Payment'
                    : 'Additional loan',
            employeeName: this.getEmployeeName(),
        };
        if (split) {
            addPayload.toInterest = split.toInterest;
            addPayload.toPrincipal = split.toPrincipal;
        }
        this.props.addTransaction(addPayload, (err) => {
            if (err) return this.showError(err);
            this.closeSheet();
            this.showSuccess(
                type === 'payment' ? 'Payment recorded' : 'Loan added',
            );
        });
    };

    submitEditBorrower = () => {
        const loan = this.getSelectedLoan();
        if (!loan) return;
        const {
            formName,
            formType,
            formDate,
            formInterestType,
            formMonthlyInterest,
            formInterestRate,
            formRatePeriod,
            formTermMonths,
            formMinimumPayment,
        } = this.state;
        if (!formName.trim()) return this.showError('Name is required');
        const type = formType === 'investor' ? 'investor' : 'personal';

        const interestType = formInterestType || 'none';
        const payload = {
            locationId: this.getLocationId(),
            borrowerId: loan.borrowerId || loan.id,
            name: formName.trim(),
            type,
            startDate: formDate,
            interestType,
            termMonths: formTermMonths ? parseInt(formTermMonths, 10) : null,
            minimumPayment: formMinimumPayment
                ? parseFloat(formMinimumPayment)
                : null,
            // Always forward these so a switch back to "none" wipes config server-side.
            monthlyInterest:
                interestType === 'fixed' ? parseFloat(formMonthlyInterest) || 0 : null,
            interestRate:
                interestType === 'percentage'
                    ? parseFloat(formInterestRate) || 0
                    : null,
            rateType:
                interestType === 'percentage'
                    ? formRatePeriod === 'annual'
                        ? 'annual'
                        : 'monthly'
                    : null,
        };

        this.props.editBorrower(payload, (err) => {
            if (err) return this.showError(err);
            this.setState({ activeTab: type });
            this.closeSheet();
            this.showSuccess('Borrower updated');
        });
    };

    handleDeleteTransaction = (entry) => {
        const loan = this.getSelectedLoan();
        if (!loan) return;

        const isPayment = entry.type === 'payment';
        const amountStr = `$${fmtMoney(entry.amount, true)}`;

        this.setState({
            confirm: {
                title: 'Delete Transaction',
                message: `This will permanently remove the ${
                    isPayment ? 'payment' : 'loan'
                } of ${amountStr} from ${fmtDate(
                    entry.date,
                )}. This action cannot be undone.`,
                confirmLabel: 'Delete',
                danger: true,
                onConfirm: () => {
                    this.setState({ confirm: null });
                    this.props.deleteTransaction(
                        {
                            locationId: this.getLocationId(),
                            borrowerId: loan.borrowerId || loan.id,
                            entryId: entry.entryId,
                        },
                        (err) => {
                            if (err) return this.showError(err);
                            this.showSuccess('Transaction deleted');
                        },
                    );
                },
            },
        });
    };

    closeConfirm = () => this.setState({ confirm: null });

    // -----------------------------------------------------------------
    handleDeleteBorrower = () => {
        const loan = this.getSelectedLoan();
        if (!loan) return;

        const txCount = (loan.transactions || []).length;
        const balance = Number(loan.balance) || 0;

        this.setState({
            confirm: {
                title: 'Delete Borrower',
                message: `This will permanently remove ${
                    loan.name
                } and all ${txCount} transaction${
                    txCount === 1 ? '' : 's'
                } (current balance $${fmtMoney(
                    balance,
                    true,
                )}). This action cannot be undone.`,
                confirmLabel: 'Delete Borrower',
                danger: true,
                onConfirm: () => {
                    this.setState({ confirm: null });
                    this.props.deleteBorrower(
                        {
                            locationId: this.getLocationId(),
                            borrowerId: loan.borrowerId || loan.id,
                        },
                        (err) => {
                            if (err) return this.showError(err);
                            // Borrower is gone — return to the list view.
                            this.setState({
                                view: 'list',
                                selectedLoanId: null,
                            });
                            this.showSuccess('Borrower deleted');
                        },
                    );
                },
            },
        });
    };

    // -----------------------------------------------------------------
    //  PDF download — generates a branded one-page loan report.
    //  Layout mirrors the on-screen PDF preview (spec section 07).
    // -----------------------------------------------------------------
    downloadPdf = () => {
        const loan = this.getSelectedLoan();
        if (!loan) return;

        try {
            const doc = new jsPDF({ unit: 'pt', format: 'letter' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;

            // ----- Header band -----
            doc.setFillColor(30, 41, 59); // navyDark
            doc.rect(0, 0, pageWidth, 90, 'F');

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(200, 200, 200);
            doc.setFontSize(9);
            doc.text('BEENEXA', margin, 32);

            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(20);
            doc.text('Loan Report', margin, 56);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(200, 200, 200);
            const dateStr = new Date().toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });
            doc.text(`${loan.name}  ·  ${dateStr}`, margin, 76);

            // ----- Loan term metadata (used by Start/End pills below) -----
            const loanStartISO = loan.startDate || loan.date || '';
            const loanTerm = Number(loan.termMonths) || 0;
            const loanEndISO =
                loanStartISO && loanTerm > 0
                    ? addMonthsISO(loanStartISO, loanTerm)
                    : '';

            // ----- Compute totals -----
            // Client-safe: "Total Paid" reflects PRINCIPAL only — interest
            // collected (loan.totalEarnedInterest) is server-side bookkeeping
            // and never appears on the borrower-facing PDF.
            const transactions = (loan.transactions || []).slice();
            const totalLoaned =
                Number(loan.totalLoaned) ||
                sumBy(transactions, (t) => t.type === 'loan');
            const principalPaid =
                Number(loan.totalPrincipalPaid) != null &&
                !Number.isNaN(Number(loan.totalPrincipalPaid))
                    ? Number(loan.totalPrincipalPaid)
                    : transactions
                          .filter((t) => t.type === 'payment')
                          .reduce(
                              (acc, t) =>
                                  acc +
                                  (t.toPrincipal != null
                                      ? Number(t.toPrincipal)
                                      : Number(t.amount) || 0),
                              0,
                          );
            const balance = totalLoaned - principalPaid;
            const pct =
                totalLoaned > 0
                    ? Math.min(
                          100,
                          Math.round((principalPaid / totalLoaned) * 100),
                      )
                    : 0;

            // ----- Start / End pills (same style as quote PDF) -----
            let cardY = 110;
            if (loanStartISO) {
                const pillW = 150;
                const pillH = 32;
                const pillGap = 10;
                const pillsTotalW = loanEndISO
                    ? pillW * 2 + pillGap
                    : pillW;
                const pillsX = pageWidth - margin - pillsTotalW;
                const pillY = cardY;
                const drawPill = (px, label, value, fg) => {
                    doc.setFillColor(255, 255, 255);
                    doc.setDrawColor(229, 231, 235);
                    doc.setLineWidth(0.6);
                    doc.roundedRect(px, pillY, pillW, pillH, 6, 6, 'FD');
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(7);
                    doc.setTextColor(100, 116, 139);
                    doc.text(label, px + 12, pillY + 12);
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(11);
                    doc.setTextColor(...fg);
                    doc.text(value || '—', px + 12, pillY + 25);
                };
                drawPill(
                    pillsX,
                    'STARTS',
                    fmtDate(loanStartISO),
                    [45, 90, 61],
                );
                if (loanEndISO) {
                    drawPill(
                        pillsX + pillW + pillGap,
                        'ENDS',
                        fmtDate(loanEndISO),
                        [198, 40, 40],
                    );
                }
                cardY += pillH + 14;
            }

            // ----- Three summary cards -----
            const cardH = 60;
            const cardGap = 12;
            const cardW = (pageWidth - margin * 2 - cardGap * 2) / 3;

            const drawCard = (x, label, amount, fill, fg) => {
                doc.setFillColor(...fill);
                doc.roundedRect(x, cardY, cardW, cardH, 8, 8, 'F');

                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(...fg);
                doc.text(label.toUpperCase(), x + 14, cardY + 20);

                doc.setFontSize(15);
                doc.text(`$${fmtMoney(amount, true)}`, x + 14, cardY + 42);
            };

            drawCard(
                margin,
                'Total Loaned',
                totalLoaned,
                [30, 41, 59],
                [255, 255, 255],
            );
            drawCard(
                margin + cardW + cardGap,
                'Total Paid',
                principalPaid,
                [232, 245, 233],
                [45, 90, 61],
            );
            drawCard(
                margin + (cardW + cardGap) * 2,
                'Balance Due',
                balance,
                [255, 235, 238],
                [198, 40, 40],
            );

            // ----- Progress bar -----
            const barY = cardY + cardH + 28;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(100, 116, 139);
            doc.text('REPAYMENT PROGRESS', margin, barY);
            doc.text(`${pct}%`, pageWidth - margin, barY, { align: 'right' });

            const trackY = barY + 8;
            const trackH = 8;
            doc.setFillColor(229, 231, 235);
            doc.roundedRect(
                margin,
                trackY,
                pageWidth - margin * 2,
                trackH,
                4,
                4,
                'F',
            );
            if (pct > 0) {
                doc.setFillColor(45, 90, 61);
                doc.roundedRect(
                    margin,
                    trackY,
                    ((pageWidth - margin * 2) * pct) / 100,
                    trackH,
                    4,
                    4,
                    'F',
                );
            }

            // ----- Transaction table (oldest → newest) -----
            const sortedTx = transactions
                .slice()
                .sort((a, b) => new Date(a.date) - new Date(b.date));

            // Client-safe rows: a split payment shows the PRINCIPAL portion as
            // the headline amount, with the total payment + interest stacked
            // below in small italic gray text. The borrower never sees a raw
            // interest-earned column.
            const tableRows = sortedTx.map((t) => {
                const isPayment = t.type === 'payment';
                const hasSplit =
                    isPayment &&
                    t.toInterest != null &&
                    Number(t.toInterest) > 0 &&
                    t.toPrincipal != null;
                const headline = hasSplit
                    ? Number(t.toPrincipal)
                    : Number(t.amount);
                const noteLines = [t.note || ''];
                if (hasSplit) {
                    noteLines.push(
                        `Total payment: $${fmtMoney(
                            t.amount,
                            true,
                        )} (interest $${fmtMoney(t.toInterest, true)})`,
                    );
                }
                const noteCell = noteLines
                    .map((s) => (s ? s : '—'))
                    .filter((s, i) => i === 0 || s !== '—')
                    .join('\n');
                return [
                    fmtDate(t.date),
                    isPayment ? 'Payment' : 'Loan',
                    noteCell,
                    `${isPayment ? '-' : '+'} $${fmtMoney(headline, true)}`,
                ];
            });

            autoTable(doc, {
                startY: trackY + trackH + 24,
                margin: { left: margin, right: margin },
                head: [['Date', 'Type', 'Note', 'Amount']],
                body: tableRows,
                theme: 'grid',
                headStyles: {
                    fillColor: [241, 245, 249],
                    textColor: [30, 41, 59],
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'left',
                },
                bodyStyles: {
                    fontSize: 9,
                    textColor: [30, 41, 59],
                    cellPadding: 6,
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                columnStyles: {
                    3: { halign: 'right', fontStyle: 'bold' },
                },
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 3) {
                        const rowData = sortedTx[data.row.index];
                        data.cell.styles.textColor =
                            rowData.type === 'payment'
                                ? [45, 90, 61]
                                : [198, 40, 40];
                    }
                    // Italic gray for the secondary line ("Pago total: ...")
                    // — stacked on the same Note cell via newline.
                    if (data.section === 'body' && data.column.index === 2) {
                        const rowData = sortedTx[data.row.index];
                        const hasSplit =
                            rowData.type === 'payment' &&
                            rowData.toInterest != null &&
                            Number(rowData.toInterest) > 0;
                        if (hasSplit) {
                            data.cell.styles.fontStyle = 'italic';
                            data.cell.styles.textColor = [120, 120, 130];
                            data.cell.styles.fontSize = 8;
                        }
                    }
                },
            });

            // ----- Outstanding balance line -----
            const finalY = doc.lastAutoTable.finalY + 24;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            doc.text(
                'OUTSTANDING BALANCE',
                pageWidth - margin,
                finalY,
                { align: 'right' },
            );
            doc.setFontSize(20);
            doc.setTextColor(198, 40, 40);
            doc.text(
                `$${fmtMoney(balance, true)}`,
                pageWidth - margin,
                finalY + 22,
                { align: 'right' },
            );

            // ----- Footer -----
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            doc.text(
                'Auto-generated by BeeNexa. Amounts in USD.',
                margin,
                pageHeight - 24,
            );
            doc.text(
                'Page 1 of 1',
                pageWidth - margin,
                pageHeight - 24,
                { align: 'right' },
            );

            // ----- Save -----
            const safeName = (loan.name || 'borrower').replace(
                /[^a-z0-9]+/gi,
                '_',
            );
            doc.save(`BeeNexa_Loan_Report_${safeName}.pdf`);
            this.showSuccess('PDF downloaded');
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('PDF generation failed', e);
            this.showError('Failed to generate PDF');
        }
    };

    // -----------------------------------------------------------------
    //  Loan Quote PDF (calculator → client-facing quote, spec section 7)
    //
    //  Hard rules:
    //    - Watermarked "No vinculante" (non-binding).
    //    - NEVER shows the internal tier reference panel.
    //    - Multi-page auto-flow when the amortization table is long.
    // -----------------------------------------------------------------
    downloadQuotePdf = () => {
        const {
            calcClient,
            calcAmount,
            calcMonths,
            calcStartDate,
            calcType,
            calcMonthlyInterest,
            calcRate,
            calcRatePeriod,
        } = this.state;

        const principal = parseFloat(calcAmount) || 0;
        const months = parseInt(calcMonths, 10) || 0;
        const fixedInt = parseFloat(calcMonthlyInterest) || 0;
        const rate = parseFloat(calcRate) || 0;
        const startDateISO = calcStartDate || todayISO();
        const firstPaymentISO = addMonthsISO(startDateISO, 1);
        const endDateISO = addMonthsISO(startDateISO, months);

        if (principal <= 0 || months <= 0) {
            return this.showError('Enter loan amount and term to generate a quote');
        }

        let monthlyPayment = 0;
        let totalInterest = 0;
        let amortization = [];
        if (calcType === 'fixed') {
            const cap = round2(principal / months);
            monthlyPayment = round2(cap + fixedInt);
            totalInterest = round2(fixedInt * months);
            amortization = buildAmortization({
                principal,
                months,
                type: 'fixed',
                monthlyInterest: fixedInt,
            });
        } else {
            if (rate <= 0) return this.showError('Enter the interest rate');
            monthlyPayment = calcAmortizationPayment(
                principal,
                months,
                rate,
                calcRatePeriod,
            );
            totalInterest = round2(monthlyPayment * months - principal);
            amortization = buildAmortization({
                principal,
                months,
                type: 'percentage',
                rate,
                period: calcRatePeriod,
            });
        }
        const totalToPay = round2(monthlyPayment * months);

        try {
            const doc = new jsPDF({ unit: 'pt', format: 'letter' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 40;

            // Header band
            doc.setFillColor(30, 41, 59);
            doc.rect(0, 0, pageWidth, 90, 'F');
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(200, 200, 200);
            doc.setFontSize(9);
            doc.text('BEENEXA', margin, 32);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(20);
            doc.text('Loan Quote', margin, 56);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(11);
            doc.setTextColor(200, 200, 200);
            const dateStr = new Date().toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            });
            doc.text(
                `Quote · Non-binding · Cotización · No vinculante  ·  ${dateStr}`,
                margin,
                76,
            );

            let y = 110;
            // For: clientName
            if (calcClient && calcClient.trim()) {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(30, 41, 59);
                doc.text(`For: ${calcClient.trim()}`, margin, y);
                y += 18;
            }

            // Interest type badge
            const isFixed = calcType === 'fixed';
            const badgeBg = isFixed ? [243, 232, 255] : [207, 250, 254];
            const badgeFg = isFixed ? [124, 58, 237] : [8, 145, 178];
            const badgeLabel = isFixed
                ? `Fixed: $${fmtMoney(fixedInt, true)}/mo`
                : `Percentage: ${fmtMoney(rate, true)}% ${
                      calcRatePeriod === 'annual' ? '/year' : '/month'
                  }`;
            doc.setFillColor(...badgeBg);
            doc.roundedRect(margin, y, 220, 22, 6, 6, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.setTextColor(...badgeFg);
            doc.text(badgeLabel, margin + 12, y + 14.5);
            y += 36;

            // Three stat cards
            const cardH = 60;
            const cardGap = 12;
            const cardW = (pageWidth - margin * 2 - cardGap * 2) / 3;
            const drawCard = (x, label, amount, fill, fg) => {
                doc.setFillColor(...fill);
                doc.roundedRect(x, y, cardW, cardH, 8, 8, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(...fg);
                doc.text(label.toUpperCase(), x + 14, y + 20);
                doc.setFontSize(15);
                doc.text(`$${fmtMoney(amount, true)}`, x + 14, y + 42);
            };
            drawCard(
                margin,
                'Loan Amount / Monto',
                principal,
                [30, 41, 59],
                [255, 255, 255],
            );
            drawCard(
                margin + cardW + cardGap,
                'Total Interest / Interés',
                totalInterest,
                badgeBg,
                badgeFg,
            );
            drawCard(
                margin + (cardW + cardGap) * 2,
                'Total to Pay / Total',
                totalToPay,
                [255, 235, 238],
                [198, 40, 40],
            );
            y += cardH + 18;

            // Hero card: Monthly Payment / Pago Mensual + Starts/Ends pills
            const heroH = 78;
            doc.setFillColor(232, 245, 233);
            doc.roundedRect(margin, y, pageWidth - margin * 2, heroH, 10, 10, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(45, 90, 61);
            doc.text('MONTHLY PAYMENT / PAGO MENSUAL', margin + 16, y + 18);
            doc.setFontSize(22);
            doc.text(
                `$${fmtMoney(monthlyPayment, true)}`,
                margin + 16,
                y + 44,
            );
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(
                `for ${months} months / por ${months} meses`,
                margin + 16,
                y + 64,
            );

            // Starts / Ends pills (right side of the hero card).
            const pillW = 130;
            const pillH = 28;
            const pillGap = 8;
            const pillsRightEdge = pageWidth - margin - 12;
            const pill2X = pillsRightEdge - pillW;
            const pill1X = pill2X - pillGap - pillW;
            const pillY = y + 14;
            const drawPill = (px, label, value, fg) => {
                doc.setFillColor(255, 255, 255);
                doc.roundedRect(px, pillY, pillW, pillH, 6, 6, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7);
                doc.setTextColor(100, 116, 139);
                doc.text(label, px + 10, pillY + 11);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(10);
                doc.setTextColor(...fg);
                doc.text(value || '—', px + 10, pillY + 23);
            };
            drawPill(pill1X, 'STARTS', fmtDate(firstPaymentISO), [45, 90, 61]);
            drawPill(pill2X, 'ENDS', fmtDate(endDateISO), [198, 40, 40]);
            y += heroH + 18;

            // Amortization table
            const tableRows = amortization.map((r) => [
                String(r.month),
                fmtDate(addMonthsISO(startDateISO, r.month)),
                `$${fmtMoney(r.payment, true)}`,
                `$${fmtMoney(r.interest, true)}`,
                `$${fmtMoney(r.principal, true)}`,
                `$${fmtMoney(r.balance, true)}`,
            ]);
            autoTable(doc, {
                startY: y,
                // Reserve room for the redesigned footer (divider + two text lines).
                margin: { left: margin, right: margin, bottom: 70 },
                head: [[
                    '#',
                    'Due Date / Fecha',
                    'Payment / Pago',
                    'Interest / Interés',
                    'Principal',
                    'Balance',
                ]],
                body: tableRows,
                theme: 'grid',
                headStyles: {
                    fillColor: [241, 245, 249],
                    textColor: [30, 41, 59],
                    fontSize: 9,
                    fontStyle: 'bold',
                    halign: 'left',
                },
                bodyStyles: {
                    fontSize: 9,
                    textColor: [30, 41, 59],
                    cellPadding: 5,
                },
                alternateRowStyles: { fillColor: [250, 250, 250] },
                columnStyles: {
                    0: { halign: 'right', cellWidth: 28 },
                    1: { halign: 'left' },
                    2: { halign: 'right' },
                    3: { halign: 'right', textColor: [124, 58, 237] },
                    4: { halign: 'right', textColor: [45, 90, 61] },
                    5: { halign: 'right', fontStyle: 'bold' },
                },
            });

            // Footer — divider, continuation hint on non-last pages,
            // bilingual disclaimer left, page count right.
            const pageCount = doc.internal.getNumberOfPages();
            for (let p = 1; p <= pageCount; p += 1) {
                doc.setPage(p);

                if (p < pageCount) {
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(9);
                    doc.setTextColor(100, 116, 139);
                    doc.text(
                        '\u00BB  This quote continues on the next page.',
                        pageWidth / 2,
                        pageHeight - 48,
                        { align: 'center' },
                    );
                }

                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.5);
                doc.line(
                    margin,
                    pageHeight - 36,
                    pageWidth - margin,
                    pageHeight - 36,
                );

                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(148, 163, 184);
                doc.text(
                    'This quote is informational and does not constitute a contract. Subject to approval.',
                    margin,
                    pageHeight - 22,
                );
                doc.text(
                    'Esta cotización es informativa y no constituye un contrato. Sujeta a aprobación.',
                    margin,
                    pageHeight - 12,
                );

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.setTextColor(100, 116, 139);
                doc.text(
                    `Page ${p} of ${pageCount}`,
                    pageWidth - margin,
                    pageHeight - 16,
                    { align: 'right' },
                );
            }

            const safeName = (calcClient && calcClient.trim()
                ? calcClient.trim()
                : 'Client'
            ).replace(/[^a-z0-9]+/gi, '_');
            doc.save(`BeeNexa_Quote_${safeName}.pdf`);
            this.showSuccess('Quote downloaded');
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Quote PDF generation failed', e);
            this.showError('Failed to generate quote PDF');
        }
    };

    // -----------------------------------------------------------------
    getFilteredBorrowers = () => {
        const { search, sortMode, activeTab, interestFilter } = this.state;
        let list = (this.props.borrowers || []).slice();

        // Tab filter: personal vs investor. Treat missing type as "personal"
        // so legacy documents still show up under the default tab.
        const tab = activeTab || 'personal';
        list = list.filter((b) => ((b.type || 'personal') === tab));

        // Interest filter — All / With Interest / No Interest (spec section 8).
        if (interestFilter === 'with') {
            list = list.filter(
                (b) => (b.interestType || 'none') !== 'none',
            );
        } else if (interestFilter === 'without') {
            list = list.filter(
                (b) => (b.interestType || 'none') === 'none',
            );
        }

        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((b) => (b.name || '').toLowerCase().includes(q));
        }

        if (sortMode === 'NAME_ASC') {
            list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        } else {
            list.sort(
                (a, b) => (Number(b.balance) || 0) - (Number(a.balance) || 0),
            );
        }

        return list;
    };

    // =================================================================
    //  RENDER — header (back arrow only on detail view)
    // =================================================================
    renderHeader() {
        // Use the shared BeeNexa CustomToolbar so the Loans module matches
        // every other module's chrome. In detail view we surface a Back
        // button as the first header action.
        const inDetail = this.state.view === 'detail';

        const headerButtons = [];
        if (inDetail) {
            headerButtons.push({
                icon: <KeyboardBackspaceIcon />,
                title: 'Back',
                handler: () =>
                    this.setState({ view: 'list', selectedLoanId: null }),
            });
        } else {
            // Calculator button — only on the list view (spec section 6).
            headerButtons.push({
                icon: <IconCalculator size={22} />,
                title: 'Loan Calculator',
                handler: this.openCalculator,
            });
        }

        return (
            <CustomToolbar
                title="Loans"
                createTitle="Loans"
                backTitle="Back to Loans"
                onClick={() => {}}
                headerButtons={headerButtons}
            />
        );
    }

    // =================================================================
    //  RENDER — list view
    // =================================================================
    renderListView() {
        const { search, sortMode, sortMenuOpen, activeTab, interestFilter } =
            this.state;
        const filtered = this.getFilteredBorrowers();
        // Tab-scoped totals so the summary card reflects the visible list.
        const total = filtered
            .map((b) => Number(b.balance) || 0)
            .filter((v) => v > 0)
            .reduce((a, b) => a + b, 0);
        const active = filtered.filter((b) => (Number(b.balance) || 0) > 0).length;

        // Interest rollup — count + monthly income — across the filtered set.
        const interestEarning = filtered.filter(
            (b) =>
                (Number(b.balance) || 0) > 0 &&
                (b.interestType || 'none') !== 'none',
        );
        const interestEarningCount = interestEarning.length;
        const monthlyInterestIncome = interestEarning.reduce(
            (acc, b) => acc + (Number(b.monthlyInterestProjected) || 0),
            0,
        );

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 64px)',
                }}
            >
                {/* Personal / Investor tab switcher — MUI secondary tabs */}
                <div
                    style={{
                        padding: '0 20px',
                        borderBottom: `1px solid ${C.border}`,
                        background: C.white,
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={(e, value) =>
                            this.setState({ activeTab: value })
                        }
                        indicatorColor="secondary"
                        textColor="secondary"
                        variant="fullWidth"
                    >
                        <Tab value="personal" label="Personal" />
                        <Tab value="investor" label="Investor" />
                    </Tabs>
                </div>

                {/* Sticky summary card */}
                <div style={{ padding: '20px 20px 0' }}>
                    <div
                        style={{
                            background: C.navyDark,
                            color: C.white,
                            borderRadius: 18,
                            padding: '20px 24px',
                            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 11,
                                        opacity: 0.7,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    Total Outstanding
                                </div>
                                <div
                                    style={{
                                        fontSize: 24,
                                        fontWeight: 700,
                                        marginTop: 4,
                                    }}
                                >
                                    ${fmtMoney(total)}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div
                                    style={{
                                        fontSize: 11,
                                        opacity: 0.7,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1.5,
                                        fontWeight: 500,
                                    }}
                                >
                                    Active Loans
                                </div>
                                <div
                                    style={{
                                        fontSize: 24,
                                        fontWeight: 700,
                                        marginTop: 4,
                                    }}
                                >
                                    {active}
                                </div>
                            </div>
                        </div>

                        {interestEarningCount > 0 && (
                            <>
                                <div
                                    style={{
                                        height: 1,
                                        background: 'rgba(255,255,255,0.14)',
                                        margin: '16px 0',
                                    }}
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontSize: 10,
                                                opacity: 0.6,
                                                textTransform: 'uppercase',
                                                letterSpacing: 1.5,
                                                fontWeight: 600,
                                            }}
                                        >
                                            Earning Interest
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 16,
                                                fontWeight: 700,
                                                marginTop: 2,
                                            }}
                                        >
                                            {interestEarningCount} loan
                                            {interestEarningCount === 1 ? '' : 's'}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div
                                            style={{
                                                fontSize: 10,
                                                opacity: 0.6,
                                                textTransform: 'uppercase',
                                                letterSpacing: 1.5,
                                                fontWeight: 600,
                                                color: '#E9D5FF',
                                            }}
                                        >
                                            Monthly Interest
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 16,
                                                fontWeight: 700,
                                                marginTop: 2,
                                                color: '#E9D5FF',
                                            }}
                                        >
                                            ${fmtMoney(monthlyInterestIncome, true)}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Interest filter tabs (spec section 8) */}
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            marginTop: 12,
                            flexWrap: 'wrap',
                        }}
                    >
                        {[
                            { id: 'all', label: 'All' },
                            { id: 'with', label: 'With Interest' },
                            { id: 'without', label: 'No Interest' },
                        ].map((opt) => {
                            const isActive = interestFilter === opt.id;
                            const activeBg =
                                opt.id === 'with' ? C.fixedBg : C.white;
                            const activeFg =
                                opt.id === 'with' ? C.fixedPrimary : C.navyDark;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() =>
                                        this.setState({
                                            interestFilter: opt.id,
                                        })
                                    }
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: 999,
                                        border: `1.5px solid ${
                                            isActive
                                                ? opt.id === 'with'
                                                    ? C.fixedPrimary
                                                    : C.primaryGreen
                                                : C.border
                                        }`,
                                        background: isActive
                                            ? activeBg
                                            : 'transparent',
                                        color: isActive
                                            ? activeFg
                                            : C.muted,
                                        fontFamily: FONT,
                                        fontSize: 12.5,
                                        fontWeight: isActive ? 700 : 500,
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Sticky search + sort bar */}
                <div
                    style={{
                        padding: '16px 20px 8px',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            position: 'relative',
                        }}
                    >
                        <span
                            style={{
                                position: 'absolute',
                                left: 14,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: C.muted,
                            }}
                        >
                            <IconSearch />
                        </span>
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={search}
                            onChange={(e) =>
                                this.setState({ search: e.target.value })
                            }
                            style={{
                                width: '100%',
                                padding: '12px 14px 12px 40px',
                                border: `2px solid ${C.border}`,
                                borderRadius: 12,
                                fontSize: 14,
                                fontFamily: FONT,
                                background: C.white,
                                outline: 'none',
                                color: C.navyDark,
                            }}
                        />
                    </div>
                    <div style={{ position: 'relative' }}>
                        <button
                            type="button"
                            onClick={() =>
                                this.setState({ sortMenuOpen: !sortMenuOpen })
                            }
                            style={{
                                background: C.white,
                                border: `2px solid ${C.border}`,
                                borderRadius: 12,
                                padding: '12px 14px',
                                cursor: 'pointer',
                                color: C.navyDark,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <IconSort />
                        </button>
                        {sortMenuOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '110%',
                                    right: 0,
                                    background: C.white,
                                    borderRadius: 12,
                                    boxShadow:
                                        '0 8px 24px rgba(15, 23, 42, 0.18)',
                                    overflow: 'hidden',
                                    zIndex: 50,
                                    minWidth: 180,
                                    border: `1px solid ${C.border}`,
                                }}
                            >
                                {[
                                    { id: 'BALANCE_DESC', label: 'Highest Balance' },
                                    { id: 'NAME_ASC', label: 'Name A–Z' },
                                ].map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() =>
                                            this.setState({
                                                sortMode: opt.id,
                                                sortMenuOpen: false,
                                            })
                                        }
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            textAlign: 'left',
                                            padding: '12px 16px',
                                            background:
                                                sortMode === opt.id
                                                    ? '#F0FDF4'
                                                    : 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontFamily: FONT,
                                            fontSize: 14,
                                            color: C.navyDark,
                                            fontWeight: sortMode === opt.id ? 600 : 400,
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Scrollable borrower list */}
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '8px 20px 120px',
                    }}
                >
                    {filtered.length === 0 ? (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: '60px 20px',
                                color: C.muted,
                            }}
                        >
                            <span
                                style={{ fontSize: 40, marginBottom: 8, display: 'block' }}
                                role="img"
                                aria-label="No borrowers"
                            >
                                📄
                            </span>
                            <div style={{ fontSize: 16, fontWeight: 600 }}>
                                No borrowers yet
                            </div>
                            <div
                                style={{
                                    fontSize: 13,
                                    marginTop: 4,
                                    opacity: 0.85,
                                }}
                            >
                                Tap the + button to add your first loan.
                            </div>
                        </div>
                    ) : (
                        filtered.map((b) => (
                            <PersonCard
                                key={b.borrowerId || b.id || b.name}
                                borrower={b}
                                onClick={() =>
                                    this.setState({
                                        view: 'detail',
                                        selectedLoanId: b.borrowerId || b.id,
                                    })
                                }
                            />
                        ))
                    )}
                </div>
            </div>
        );
    }

    // =================================================================
    //  RENDER — interest config block (used in new + edit borrower sheets)
    //
    //  Three-option pill (None / Fixed $ / % Rate). Conditional purple
    //  block for fixed (live preview of capital/mo + total). Conditional
    //  cyan block for percentage with rate + monthly/annual toggle.
    //  Bilingual high-risk warning + dark internal tier panel when monthly
    //  rate is selected (spec sections 03 & 04).
    // =================================================================
    renderInterestConfigBlock() {
        const {
            formInterestType,
            formMonthlyInterest,
            formInterestRate,
            formRatePeriod,
            formTermMonths,
            formMinimumPayment,
            formAmount,
        } = this.state;

        const pill = (id, label, accentBg, accentFg) => {
            const isActive = formInterestType === id;
            const bg = isActive ? accentBg : C.white;
            const fg = isActive ? accentFg : C.muted;
            const border = isActive ? accentFg : C.border;
            return (
                <button
                    key={id}
                    type="button"
                    onClick={() =>
                        this.setState({ formInterestType: id })
                    }
                    style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: `2px solid ${border}`,
                        background: bg,
                        color: fg,
                        fontFamily: FONT,
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                >
                    {label}
                </button>
            );
        };

        // Live preview math for fixed type.
        const principal = parseFloat(formAmount) || 0;
        const term = parseInt(formTermMonths, 10) || 0;
        const fixedInterest = parseFloat(formMonthlyInterest) || 0;
        const cap = term > 0 ? round2(principal / term) : 0;
        const totalMo = round2(cap + fixedInterest);

        return (
            <div style={{ marginBottom: 14 }}>
                <label
                    style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.muted,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        marginBottom: 6,
                    }}
                >
                    Interest Type
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                    {pill('none', 'None', '#F1F5F9', C.navyDark)}
                    {pill('fixed', 'Fixed $', C.fixedBg, C.fixedPrimary)}
                    {pill('percentage', '% Rate', C.pctBg, C.pctPrimary)}
                </div>

                {formInterestType === 'fixed' && (
                    <div
                        style={{
                            background: C.fixedBg,
                            borderRadius: 14,
                            padding: '14px 14px 4px',
                            marginTop: 12,
                        }}
                    >
                        <Field
                            label="Monthly Interest Charge / Cargo Mensual"
                            type="number"
                            prefix="$"
                            value={formMonthlyInterest}
                            onChange={(v) =>
                                this.setState({ formMonthlyInterest: v })
                            }
                        />
                        <Field
                            label="Term (months) / Plazo (meses)"
                            type="number"
                            value={formTermMonths}
                            onChange={(v) =>
                                this.setState({ formTermMonths: v })
                            }
                        />
                        {principal > 0 && term > 0 && (
                            <div
                                style={{
                                    background: C.white,
                                    borderRadius: 12,
                                    padding: '12px 14px',
                                    marginBottom: 12,
                                    border: `1px solid ${C.border}`,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: 13,
                                        marginBottom: 4,
                                    }}
                                >
                                    <span style={{ color: C.muted }}>
                                        Principal/mo ({term} months)
                                    </span>
                                    <span
                                        style={{
                                            color: C.primaryGreen,
                                            fontWeight: 700,
                                        }}
                                    >
                                        ${fmtMoney(cap, true)}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: 13,
                                        marginBottom: 8,
                                    }}
                                >
                                    <span style={{ color: C.muted }}>
                                        + Fixed Interest / Interés Fijo
                                    </span>
                                    <span
                                        style={{
                                            color: C.fixedPrimary,
                                            fontWeight: 700,
                                        }}
                                    >
                                        ${fmtMoney(fixedInterest, true)}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        height: 1,
                                        background: C.border,
                                        margin: '6px 0',
                                    }}
                                />
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: 15,
                                    }}
                                >
                                    <span
                                        style={{
                                            color: C.navyDark,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Total Monthly / Pago Mensual
                                    </span>
                                    <span
                                        style={{
                                            color: C.navyDark,
                                            fontWeight: 700,
                                            fontSize: 17,
                                        }}
                                    >
                                        ${fmtMoney(totalMo, true)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {formInterestType === 'percentage' && (
                    <div
                        style={{
                            background: C.pctBg,
                            borderRadius: 14,
                            padding: '14px 14px 4px',
                            marginTop: 12,
                        }}
                    >
                        <Field
                            label="Interest Rate"
                            type="number"
                            prefix="%"
                            value={formInterestRate}
                            onChange={(v) =>
                                this.setState({ formInterestRate: v })
                            }
                        />
                        <label
                            style={{
                                display: 'block',
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                marginBottom: 6,
                            }}
                        >
                            Rate Period
                        </label>
                        <div
                            style={{
                                display: 'flex',
                                gap: 6,
                                marginBottom: 14,
                            }}
                        >
                            {[
                                { id: 'monthly', label: 'Per Month' },
                                { id: 'annual', label: 'Per Year' },
                            ].map((opt) => {
                                const isActive = formRatePeriod === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() =>
                                            this.setState({
                                                formRatePeriod: opt.id,
                                            })
                                        }
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            borderRadius: 10,
                                            border: `2px solid ${
                                                isActive
                                                    ? C.pctPrimary
                                                    : C.border
                                            }`,
                                            background: isActive
                                                ? C.pctPrimary
                                                : C.white,
                                            color: isActive
                                                ? C.white
                                                : C.navyDark,
                                            fontFamily: FONT,
                                            fontSize: 13,
                                            fontWeight: isActive ? 700 : 500,
                                            cursor: 'pointer',
                                            outline: 'none',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        <Field
                            label="Term (months) / Plazo (meses)"
                            type="number"
                            value={formTermMonths}
                            onChange={(v) =>
                                this.setState({ formTermMonths: v })
                            }
                        />
                        <Field
                            label="Minimum Monthly Payment / Pago Mínimo"
                            type="number"
                            prefix="$"
                            value={formMinimumPayment}
                            onChange={(v) =>
                                this.setState({ formMinimumPayment: v })
                            }
                        />
                        {formRatePeriod === 'monthly' && (
                            <>
                                <HighRiskWarning />
                                <TierPanel />
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // =================================================================
    //  RENDER — payment split preview (spec section 5)
    //  Shows int/cap split before user confirms. Orange warning when
    //  payment doesn't cover full interest. Abono toggle forces 100% cap.
    // =================================================================
    renderPaymentSplitPreview(loan) {
        const { formAmount, formPaymentMode } = this.state;
        const amt = parseFloat(formAmount) || 0;
        const intDue = calcInterestDue(loan);

        let toInterest;
        let toPrincipal;
        if (formPaymentMode === 'abono') {
            toInterest = 0;
            toPrincipal = amt;
        } else {
            const split = splitPayment(amt, intDue);
            toInterest = split.toInterest;
            toPrincipal = split.toPrincipal;
        }
        const undercoversInterest =
            formPaymentMode === 'auto' && amt > 0 && amt < intDue;

        return (
            <div style={{ marginBottom: 16 }}>
                <div
                    style={{
                        display: 'flex',
                        gap: 6,
                        marginBottom: 10,
                    }}
                >
                    {[
                        { id: 'auto', label: 'Auto Split' },
                        { id: 'abono', label: 'Abono (capital only)' },
                    ].map((opt) => {
                        const isActive = formPaymentMode === opt.id;
                        return (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() =>
                                    this.setState({
                                        formPaymentMode: opt.id,
                                    })
                                }
                                style={{
                                    flex: 1,
                                    padding: '8px 12px',
                                    borderRadius: 10,
                                    border: `2px solid ${
                                        isActive
                                            ? C.primaryGreen
                                            : C.border
                                    }`,
                                    background: isActive
                                        ? C.primaryGreen
                                        : C.white,
                                    color: isActive ? C.white : C.muted,
                                    fontFamily: FONT,
                                    fontSize: 12.5,
                                    fontWeight: isActive ? 700 : 500,
                                    cursor: 'pointer',
                                    outline: 'none',
                                }}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
                <div
                    style={{
                        background: '#F8FAFC',
                        border: `1px solid ${C.border}`,
                        borderRadius: 12,
                        padding: '12px 14px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 13,
                            marginBottom: 4,
                        }}
                    >
                        <span style={{ color: C.muted }}>→ Interest</span>
                        <span
                            style={{
                                color: C.fixedPrimary,
                                fontWeight: 700,
                            }}
                        >
                            ${fmtMoney(toInterest, true)}
                        </span>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 13,
                        }}
                    >
                        <span style={{ color: C.muted }}>→ Principal</span>
                        <span
                            style={{
                                color: C.primaryGreen,
                                fontWeight: 700,
                            }}
                        >
                            ${fmtMoney(toPrincipal, true)}
                        </span>
                    </div>
                </div>
                {undercoversInterest && (
                    <div
                        style={{
                            background: C.warningBg,
                            border: `1.5px solid ${C.warning}`,
                            color: C.warningDark,
                            borderRadius: 10,
                            padding: '10px 12px',
                            marginTop: 8,
                            fontSize: 12.5,
                            fontWeight: 600,
                            lineHeight: 1.5,
                        }}
                    >
                        Payment doesn&apos;t cover full interest ($
                        {fmtMoney(intDue, true)}). Principal is not reduced.
                        <div
                            style={{
                                fontStyle: 'italic',
                                fontWeight: 500,
                                marginTop: 4,
                                opacity: 0.85,
                            }}
                        >
                            Pago no cubre interés completo. No se reduce
                            capital.
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // =================================================================
    //  RENDER — calculator bottom sheet (spec section 6)
    // =================================================================
    renderCalculatorSheet() {
        const {
            calcClient,
            calcAmount,
            calcMonths,
            calcStartDate,
            calcType,
            calcMonthlyInterest,
            calcRate,
            calcRatePeriod,
        } = this.state;

        const principal = parseFloat(calcAmount) || 0;
        const months = parseInt(calcMonths, 10) || 0;
        const fixedInt = parseFloat(calcMonthlyInterest) || 0;
        const rate = parseFloat(calcRate) || 0;
        const startDateISO = calcStartDate || todayISO();
        // First payment is one month after the loan starts; the loan ends
        // when the final scheduled payment falls due.
        const firstPaymentISO = months > 0 ? addMonthsISO(startDateISO, 1) : '';
        const endDateISO = months > 0 ? addMonthsISO(startDateISO, months) : '';

        let monthlyPayment = 0;
        let totalInterest = 0;
        let amortization = [];
        if (calcType === 'fixed' && principal > 0 && months > 0) {
            const cap = round2(principal / months);
            monthlyPayment = round2(cap + fixedInt);
            totalInterest = round2(fixedInt * months);
            amortization = buildAmortization({
                principal,
                months,
                type: 'fixed',
                monthlyInterest: fixedInt,
            });
        } else if (
            calcType === 'percentage' &&
            principal > 0 &&
            months > 0 &&
            rate > 0
        ) {
            monthlyPayment = calcAmortizationPayment(
                principal,
                months,
                rate,
                calcRatePeriod,
            );
            totalInterest = round2(monthlyPayment * months - principal);
            amortization = buildAmortization({
                principal,
                months,
                type: 'percentage',
                rate,
                period: calcRatePeriod,
            });
        }
        const totalToPay = round2(monthlyPayment * months);
        const valid = monthlyPayment > 0;

        const typePill = (id, label, accentBg, accentFg) => {
            const isActive = calcType === id;
            return (
                <button
                    key={id}
                    type="button"
                    onClick={() => this.setState({ calcType: id })}
                    style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: `2px solid ${
                            isActive ? accentFg : C.border
                        }`,
                        background: isActive ? accentBg : C.white,
                        color: isActive ? accentFg : C.muted,
                        fontFamily: FONT,
                        fontSize: 13,
                        fontWeight: isActive ? 700 : 500,
                        cursor: 'pointer',
                        outline: 'none',
                    }}
                >
                    {label}
                </button>
            );
        };

        return (
            <BottomSheet
                open
                onClose={this.closeSheet}
                title="Loan Calculator"
            >
                <Field
                    label="Client / Cliente (optional)"
                    value={calcClient}
                    onChange={(v) => this.setState({ calcClient: v })}
                />
                <Field
                    label="Loan Amount / Monto Solicitado"
                    type="number"
                    prefix="$"
                    value={calcAmount}
                    onChange={(v) => this.setState({ calcAmount: v })}
                />
                <Field
                    label="Term (months) / Plazo (meses)"
                    type="number"
                    value={calcMonths}
                    onChange={(v) => this.setState({ calcMonths: v })}
                />
                <Field
                    label="Start Date / Fecha de Inicio"
                    type="date"
                    value={calcStartDate}
                    onChange={(v) => this.setState({ calcStartDate: v })}
                />
                <label
                    style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.muted,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        marginBottom: 6,
                    }}
                >
                    Interest Type / Tipo de Interés
                </label>
                <div
                    style={{
                        display: 'flex',
                        gap: 6,
                        marginBottom: 14,
                    }}
                >
                    {typePill('fixed', 'Fixed $', C.fixedBg, C.fixedPrimary)}
                    {typePill(
                        'percentage',
                        '% Rate',
                        C.pctBg,
                        C.pctPrimary,
                    )}
                </div>
                {calcType === 'fixed' ? (
                    <div
                        style={{
                            background: C.fixedBg,
                            borderRadius: 14,
                            padding: '14px 14px 4px',
                            marginBottom: 14,
                        }}
                    >
                        <Field
                            label="Monthly Interest Charge / Cargo Mensual"
                            type="number"
                            prefix="$"
                            value={calcMonthlyInterest}
                            onChange={(v) =>
                                this.setState({ calcMonthlyInterest: v })
                            }
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            background: C.pctBg,
                            borderRadius: 14,
                            padding: '14px 14px 4px',
                            marginBottom: 14,
                        }}
                    >
                        <Field
                            label="Interest Rate / Tasa de Interés"
                            type="number"
                            prefix="%"
                            value={calcRate}
                            onChange={(v) =>
                                this.setState({ calcRate: v })
                            }
                        />
                        <label
                            style={{
                                display: 'block',
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                marginBottom: 6,
                            }}
                        >
                            Period / Período
                        </label>
                        <div
                            style={{
                                display: 'flex',
                                gap: 6,
                                marginBottom: 14,
                            }}
                        >
                            {[
                                { id: 'monthly', label: 'Per Month' },
                                { id: 'annual', label: 'Per Year' },
                            ].map((opt) => {
                                const isActive =
                                    calcRatePeriod === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() =>
                                            this.setState({
                                                calcRatePeriod: opt.id,
                                            })
                                        }
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            borderRadius: 10,
                                            border: `2px solid ${
                                                isActive
                                                    ? C.pctPrimary
                                                    : C.border
                                            }`,
                                            background: isActive
                                                ? C.pctPrimary
                                                : C.white,
                                            color: isActive
                                                ? C.white
                                                : C.navyDark,
                                            fontFamily: FONT,
                                            fontSize: 13,
                                            fontWeight: isActive ? 700 : 500,
                                            cursor: 'pointer',
                                            outline: 'none',
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        {calcRatePeriod === 'monthly' && (
                            <>
                                <HighRiskWarning />
                                <TierPanel />
                            </>
                        )}
                    </div>
                )}

                {valid && (
                    <div
                        style={{
                            background: C.white,
                            border: `1px solid ${C.border}`,
                            borderRadius: 14,
                            padding: '14px 16px',
                            marginBottom: 14,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 6,
                            }}
                        >
                            <span style={{ color: C.muted, fontSize: 13 }}>
                                Monthly Payment / Pago Mensual
                            </span>
                            <span
                                style={{
                                    color: C.primaryGreen,
                                    fontWeight: 700,
                                    fontSize: 18,
                                }}
                            >
                                ${fmtMoney(monthlyPayment, true)}
                            </span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 6,
                            }}
                        >
                            <span style={{ color: C.muted, fontSize: 13 }}>
                                Total Interest / Interés ({months} months)
                            </span>
                            <span
                                style={{
                                    color: C.fixedPrimary,
                                    fontWeight: 700,
                                }}
                            >
                                ${fmtMoney(totalInterest, true)}
                            </span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span style={{ color: C.muted, fontSize: 13 }}>
                                Total to Pay / Total a Pagar
                            </span>
                            <span
                                style={{
                                    color: C.deleteRed,
                                    fontWeight: 700,
                                }}
                            >
                                ${fmtMoney(totalToPay, true)}
                            </span>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                gap: 8,
                                marginTop: 12,
                            }}
                        >
                            <div
                                style={{
                                    flex: 1,
                                    background: '#F1F5F9',
                                    borderRadius: 10,
                                    padding: '10px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                <span role="img" aria-label="calendar" style={{ fontSize: 18 }}>📅</span>
                                <div>
                                    <div
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: C.muted,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.8,
                                        }}
                                    >
                                        Starts
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: C.primaryGreen,
                                        }}
                                    >
                                        {fmtDate(firstPaymentISO) || '—'}
                                    </div>
                                </div>
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    background: '#FEF2F2',
                                    borderRadius: 10,
                                    padding: '10px 12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                }}
                            >
                                <span role="img" aria-label="finish flag" style={{ fontSize: 18 }}>🏁</span>
                                <div>
                                    <div
                                        style={{
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: C.muted,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.8,
                                        }}
                                    >
                                        Ends
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: C.deleteRed,
                                        }}
                                    >
                                        {fmtDate(endDateISO) || '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {valid && amortization.length > 0 && (
                    <div
                        style={{
                            background: C.white,
                            border: `1px solid ${C.border}`,
                            borderRadius: 12,
                            padding: '4px 0',
                            maxHeight: 220,
                            overflowY: 'auto',
                            marginBottom: 14,
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '40px 1fr 1fr 1fr 1fr',
                                fontSize: 11,
                                fontWeight: 700,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                padding: '8px 12px',
                                borderBottom: `1px solid ${C.border}`,
                            }}
                        >
                            <span>#</span>
                            <span>Payment</span>
                            <span>Interest</span>
                            <span>Principal</span>
                            <span style={{ textAlign: 'right' }}>
                                Balance
                            </span>
                        </div>
                        {amortization.map((row) => (
                            <div
                                key={row.month}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        '40px 1fr 1fr 1fr 1fr',
                                    fontSize: 12,
                                    padding: '6px 12px',
                                    color: C.navyDark,
                                }}
                            >
                                <span style={{ color: C.muted }}>
                                    {row.month}
                                </span>
                                <span>${fmtMoney(row.payment, true)}</span>
                                <span style={{ color: C.fixedPrimary }}>
                                    ${fmtMoney(row.interest, true)}
                                </span>
                                <span style={{ color: C.primaryGreen }}>
                                    ${fmtMoney(row.principal, true)}
                                </span>
                                <span style={{ textAlign: 'right' }}>
                                    ${fmtMoney(row.balance, true)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button"
                        onClick={
                            valid ? () => this.downloadQuotePdf() : undefined
                        }
                        disabled={!valid}
                        style={{
                            flex: 1,
                            background: valid ? C.navyDark : C.border,
                            color: C.white,
                            border: 'none',
                            padding: '14px 20px',
                            borderRadius: 12,
                            fontSize: 14,
                            fontWeight: 700,
                            fontFamily: FONT,
                            cursor: valid ? 'pointer' : 'not-allowed',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                        }}
                    >
                        <IconDownload size={16} /> Quote PDF
                    </button>
                </div>
            </BottomSheet>
        );
    }

    // =================================================================
    //  RENDER — interest info card (spec sections 03 & 04)
    //  Purple gradient for fixed; cyan gradient for percentage.
    // =================================================================
    renderInterestInfoCard(loan) {
        const isFixed = loan.interestType === 'fixed';
        const primary = isFixed ? C.fixedPrimary : C.pctPrimary;
        const gradient = isFixed
            ? 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)'
            : 'linear-gradient(135deg, #0891B2 0%, #0E7490 100%)';

        const term = Number(loan.termMonths) || 0;
        const earned = Number(loan.totalEarnedInterest) || 0;

        const capPerMo = isFixed ? capitalPerMonth(loan) : null;
        const intDue = calcInterestDue(loan);
        const totalMo = isFixed
            ? round2((capPerMo || 0) + (Number(loan.monthlyInterest) || 0))
            : null;
        const ratePeriodLabel = loan.rateType === 'annual' ? 'yr' : 'mo';

        const cell = (label, value, accent) => (
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 10,
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        letterSpacing: 1.2,
                        fontWeight: 700,
                    }}
                >
                    {label}
                </div>
                <div
                    style={{
                        fontSize: 17,
                        fontWeight: 700,
                        marginTop: 4,
                        color: accent || C.white,
                    }}
                >
                    {value}
                </div>
            </div>
        );

        return (
            <div
                style={{
                    background: gradient,
                    color: C.white,
                    borderRadius: 18,
                    padding: '20px 22px',
                    marginBottom: 20,
                    boxShadow: `0 10px 28px ${primary}33`,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        gap: 14,
                        alignItems: 'flex-start',
                    }}
                >
                    {isFixed ? (
                        <>
                            {cell(
                                'Principal/mo',
                                `$${fmtMoney(capPerMo, true)}`,
                            )}
                            {cell(
                                'Interest/mo',
                                `$${fmtMoney(loan.monthlyInterest, true)}`,
                                '#FDE68A',
                            )}
                            {cell(
                                'Total/mo',
                                `$${fmtMoney(totalMo, true)}`,
                            )}
                        </>
                    ) : (
                        <>
                            {cell(
                                'Rate',
                                `${fmtMoney(
                                    loan.interestRate,
                                    true,
                                )}% / ${ratePeriodLabel}`,
                            )}
                            {cell(
                                'Interest Due',
                                `$${fmtMoney(intDue, true)}`,
                                '#A5F3FC',
                            )}
                            {cell(
                                'Min Payment',
                                loan.minimumPayment != null
                                    ? `$${fmtMoney(loan.minimumPayment, true)}`
                                    : '—',
                            )}
                        </>
                    )}
                </div>
                <div
                    style={{
                        height: 1,
                        background: 'rgba(255,255,255,0.18)',
                        margin: '14px 0',
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 12.5,
                    }}
                >
                    <div>
                        <span style={{ opacity: 0.7 }}>Term / Plazo: </span>
                        <span style={{ fontWeight: 700 }}>
                            {term > 0 ? `${term} months` : '—'}
                        </span>
                    </div>
                    <div>
                        <span style={{ opacity: 0.7 }}>Earned: </span>
                        <span style={{ fontWeight: 700 }}>
                            ${fmtMoney(earned, true)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // =================================================================
    //  RENDER — detail view
    // =================================================================
    renderDetailView() {
        const loan = this.getSelectedLoan();
        if (!loan) {
            return (
                <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>
                    Borrower not found.
                </div>
            );
        }

        const balance = Number(loan.balance) || 0;
        const transactions = (loan.transactions || []).slice().sort(
            (a, b) => new Date(b.date) - new Date(a.date),
        );
        const isAdmin = this.isAdmin();
        const canEdit = this.canEdit();

        const detailStartISO = loan.startDate || loan.date || '';
        const detailTerm = Number(loan.termMonths) || 0;
        const detailEndISO =
            detailStartISO && detailTerm > 0
                ? addMonthsISO(detailStartISO, detailTerm)
                : '';

        return (
            <div
                style={{
                    padding: '24px 24px 120px',
                    animation: 'bnSlideInRight 0.25s ease',
                }}
            >
                {/* Hero card — borrower identity + balance + term */}
                <div
                    style={{
                        background: C.white,
                        borderRadius: 20,
                        padding: '24px 28px',
                        marginBottom: 20,
                        boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
                        border: `1px solid ${C.border}`,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Subtle accent stripe */}
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background: `linear-gradient(90deg, ${C.primaryGreen} 0%, ${C.headerGreen} 100%)`,
                        }}
                    />

                    {/* Top row: name + badge (left) | edit/delete (right) */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: 12,
                            marginBottom: 18,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                                minWidth: 0,
                                flex: 1,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 22,
                                    fontWeight: 700,
                                    color: C.navyDark,
                                    letterSpacing: -0.3,
                                    lineHeight: 1.2,
                                }}
                            >
                                {loan.name}
                            </div>
                            <InterestBadge loan={loan} size="lg" />
                        </div>
                        {(canEdit || isAdmin) && (
                            <div
                                style={{
                                    display: 'inline-flex',
                                    gap: 6,
                                    flexShrink: 0,
                                }}
                            >
                                {canEdit && (
                                    <button
                                        type="button"
                                        onClick={this.openEditBorrowerSheet}
                                        title="Edit borrower"
                                        style={{
                                            background: 'transparent',
                                            border: `1px solid ${C.border}`,
                                            cursor: 'pointer',
                                            width: 34,
                                            height: 34,
                                            borderRadius: 10,
                                            color: C.muted,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#F8FAFC';
                                            e.currentTarget.style.color = C.navyDark;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = C.muted;
                                        }}
                                    >
                                        <IconEdit size={15} />
                                    </button>
                                )}
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={this.handleDeleteBorrower}
                                        title="Delete borrower"
                                        style={{
                                            background: 'transparent',
                                            border: `1px solid ${C.border}`,
                                            cursor: 'pointer',
                                            width: 34,
                                            height: 34,
                                            borderRadius: 10,
                                            color: C.deleteRed,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.15s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#FEF2F2';
                                            e.currentTarget.style.borderColor = '#FECACA';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.borderColor = C.border;
                                        }}
                                    >
                                        <IconTrash size={15} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Current Balance — prominent, left-aligned */}
                    <div>
                        <div
                            style={{
                                fontSize: 10.5,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 1.6,
                                fontWeight: 700,
                                marginBottom: 6,
                            }}
                        >
                            Current Balance
                        </div>
                        <div
                            style={{
                                fontSize: 40,
                                fontWeight: 800,
                                color: balance >= 0 ? C.primaryGreen : C.deleteRed,
                                letterSpacing: -1,
                                lineHeight: 1,
                                fontFeatureSettings: '"tnum"',
                            }}
                        >
                            ${fmtMoney(balance, true)}
                        </div>
                    </div>

                    {/* Term footer — subtle divider, side-by-side dates (single line, no wrap) */}
                    {detailStartISO && (
                        <div
                            style={{
                                marginTop: 22,
                                paddingTop: 18,
                                borderTop: `1px solid ${C.border}`,
                                display: 'flex',
                                gap: 12,
                                flexWrap: 'nowrap',
                                alignItems: 'stretch',
                            }}
                        >
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        fontSize: 9.5,
                                        color: C.muted,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        fontWeight: 700,
                                        marginBottom: 5,
                                    }}
                                >
                                    <svg
                                        width="11"
                                        height="11"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke={C.primaryGreen}
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect x="3" y="4" width="18" height="18" rx="2" />
                                        <path d="M16 2v4 M8 2v4 M3 10h18" />
                                    </svg>
                                    Starts
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 700,
                                        color: C.navyDark,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {fmtDate(detailStartISO)}
                                </div>
                            </div>
                            {detailEndISO && (
                                <div
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        borderLeft: `1px solid ${C.border}`,
                                        paddingLeft: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            fontSize: 9.5,
                                            color: C.muted,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            fontWeight: 700,
                                            marginBottom: 5,
                                        }}
                                    >
                                        <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={C.deleteRed}
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M4 22V4 M4 4l13 0 -3 5 3 5 -13 0" />
                                        </svg>
                                        Ends
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: C.navyDark,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {fmtDate(detailEndISO)}
                                    </div>
                                </div>
                            )}
                            {detailEndISO && detailTerm > 0 && (
                                <div
                                    style={{
                                        flexShrink: 0,
                                        borderLeft: `1px solid ${C.border}`,
                                        paddingLeft: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            fontSize: 9.5,
                                            color: C.muted,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            fontWeight: 700,
                                            marginBottom: 5,
                                        }}
                                    >
                                        <svg
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke={C.fixedPrimary}
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 6v6l4 2" />
                                        </svg>
                                        Term
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: C.navyDark,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {detailTerm} mo
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Action pills — single row, no wrap */}
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                        marginBottom: 28,
                        width: '100%',
                    }}
                >
                    <ActionPill
                        icon={<IconDollar />}
                        label="Record Payment"
                        color={C.paymentGreen}
                        onClick={() => this.openSheet('payment')}
                    />
                    <ActionPill
                        icon={<IconPlus />}
                        label="Add to Loan"
                        color={C.loanRed}
                        onClick={() => this.openSheet('loan')}
                    />
                    <ActionPill
                        icon={<IconDownload />}
                        label="PDF"
                        color={C.muted}
                        onClick={this.downloadPdf}
                    />
                </div>

                {/* Interest info card (spec section 03/04) */}
                {(loan.interestType || 'none') !== 'none' &&
                    this.renderInterestInfoCard(loan)}

                {/* Transactions list */}
                <div
                    style={{
                        background: C.white,
                        borderRadius: 16,
                        padding: '8px 20px 20px',
                        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                    }}
                >
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 1.5,
                            padding: '14px 0 8px',
                        }}
                    >
                        Transaction History
                    </div>
                    {transactions.length === 0 ? (
                        <div
                            style={{
                                padding: '24px 0',
                                textAlign: 'center',
                                color: C.muted,
                                fontSize: 14,
                            }}
                        >
                            No transactions yet.
                        </div>
                    ) : (
                        transactions.map((t) => (
                            <TxRow
                                key={t.entryId}
                                tx={t}
                                onEdit={
                                    canEdit
                                        ? () => this.openEditTransactionSheet(t)
                                        : null
                                }
                                onDelete={
                                    isAdmin
                                        ? () => this.handleDeleteTransaction(t)
                                        : null
                                }
                            />
                        ))
                    )}
                </div>
            </div>
        );
    }

    // =================================================================
    //  RENDER — bottom sheets
    // =================================================================
    renderSheets() {
        const {
            sheet,
            formDate,
            formAmount,
            formNote,
            formName,
            editingEntryId,
        } = this.state;
        const isEditing = !!editingEntryId;

        if (sheet === 'payment') {
            const loan = this.getSelectedLoan();
            const isInterestBearing =
                loan && (loan.interestType || 'none') !== 'none';
            return (
                <BottomSheet
                    open
                    onClose={this.closeSheet}
                    title={isEditing ? 'Edit Payment' : 'Record Payment'}
                >
                    <Field
                        label="Date"
                        type="date"
                        value={formDate}
                        onChange={(v) => this.setState({ formDate: v })}
                    />
                    <Field
                        label="Amount"
                        type="number"
                        prefix="$"
                        value={formAmount}
                        onChange={(v) => this.setState({ formAmount: v })}
                    />
                    {isInterestBearing &&
                        this.renderPaymentSplitPreview(loan)}
                    <Field
                        label="Note"
                        multiline
                        value={formNote}
                        onChange={(v) => this.setState({ formNote: v })}
                    />
                    <SubmitBtn
                        label={isEditing ? 'Save Changes' : 'Record Payment'}
                        color={C.paymentGreen}
                        onClick={() => this.submitTransaction('payment')}
                    />
                </BottomSheet>
            );
        }

        if (sheet === 'loan') {
            return (
                <BottomSheet
                    open
                    onClose={this.closeSheet}
                    title={isEditing ? 'Edit Loan' : 'Add to Loan'}
                >
                    <Field
                        label="Date"
                        type="date"
                        value={formDate}
                        onChange={(v) => this.setState({ formDate: v })}
                    />
                    <Field
                        label="Amount"
                        type="number"
                        prefix="$"
                        value={formAmount}
                        onChange={(v) => this.setState({ formAmount: v })}
                    />
                    <Field
                        label="Note"
                        multiline
                        value={formNote}
                        onChange={(v) => this.setState({ formNote: v })}
                    />
                    <SubmitBtn
                        label={isEditing ? 'Save Changes' : 'Add to Loan'}
                        color={C.loanRed}
                        onClick={() => this.submitTransaction('loan')}
                    />
                </BottomSheet>
            );
        }

        if (sheet === 'editBorrower') {
            const { formType } = this.state;
            return (
                <BottomSheet open onClose={this.closeSheet} title="Edit Borrower">
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            marginBottom: 14,
                        }}
                    >
                        {[
                            { id: 'personal', label: 'Personal' },
                            { id: 'investor', label: 'Investor' },
                        ].map((opt) => {
                            const isActive = formType === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() =>
                                        this.setState({ formType: opt.id })
                                    }
                                    style={{
                                        flex: 1,
                                        padding: '12px 14px',
                                        borderRadius: 12,
                                        border: `2px solid ${
                                            isActive
                                                ? C.primaryGreen
                                                : C.border
                                        }`,
                                        background: isActive
                                            ? C.primaryGreen
                                            : C.white,
                                        color: isActive ? C.white : C.navyDark,
                                        fontFamily: FONT,
                                        fontSize: 14,
                                        fontWeight: isActive ? 700 : 500,
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    <Field
                        label="Borrower Name"
                        value={formName}
                        onChange={(v) => this.setState({ formName: v })}
                    />
                    <Field
                        label="Start Date / Fecha de Inicio"
                        type="date"
                        value={formDate}
                        onChange={(v) => this.setState({ formDate: v })}
                    />
                    {this.renderInterestConfigBlock()}
                    <SubmitBtn
                        label="Save Changes"
                        color={C.primaryGreen}
                        onClick={this.submitEditBorrower}
                    />
                </BottomSheet>
            );
        }

        if (sheet === 'new') {
            const { formType } = this.state;
            return (
                <BottomSheet open onClose={this.closeSheet} title="New Loan">
                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            marginBottom: 14,
                        }}
                    >
                        {[
                            { id: 'personal', label: 'Personal' },
                            { id: 'investor', label: 'Investor' },
                        ].map((opt) => {
                            const isActive = formType === opt.id;
                            return (
                                <button
                                    key={opt.id}
                                    type="button"
                                    onClick={() =>
                                        this.setState({ formType: opt.id })
                                    }
                                    style={{
                                        flex: 1,
                                        padding: '12px 14px',
                                        borderRadius: 12,
                                        border: `2px solid ${
                                            isActive
                                                ? C.primaryGreen
                                                : C.border
                                        }`,
                                        background: isActive
                                            ? C.primaryGreen
                                            : C.white,
                                        color: isActive ? C.white : C.navyDark,
                                        fontFamily: FONT,
                                        fontSize: 14,
                                        fontWeight: isActive ? 700 : 500,
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                    <Field
                        label="Borrower Name"
                        value={formName}
                        onChange={(v) => this.setState({ formName: v })}
                    />
                    <Field
                        label="Date"
                        type="date"
                        value={formDate}
                        onChange={(v) => this.setState({ formDate: v })}
                    />
                    <Field
                        label="Loan Amount"
                        type="number"
                        prefix="$"
                        value={formAmount}
                        onChange={(v) => this.setState({ formAmount: v })}
                    />
                    {this.renderInterestConfigBlock()}
                    <Field
                        label="Note"
                        multiline
                        value={formNote}
                        onChange={(v) => this.setState({ formNote: v })}
                    />
                    <SubmitBtn
                        label="Create Loan"
                        color={C.primaryGreen}
                        onClick={this.submitNewLoan}
                    />
                </BottomSheet>
            );
        }

        if (sheet === 'calculator') {
            return this.renderCalculatorSheet();
        }

        return null;
    }

    // =================================================================
    //  RENDER — PDF preview overlay  (spec section 07)
    // =================================================================
    renderPdfOverlay() {
        if (!this.state.showPdf) return null;
        const loan = this.getSelectedLoan();
        if (!loan) return null;

        const totalLoaned =
            Number(loan.totalLoaned) ||
            sumBy(loan.transactions || [], (t) => t.type === 'loan');
        // Client-safe: principal-only "Total Paid" — interest collected is
        // never surfaced in this overlay (matches the PDF download).
        const principalPaid =
            loan.totalPrincipalPaid != null &&
            !Number.isNaN(Number(loan.totalPrincipalPaid))
                ? Number(loan.totalPrincipalPaid)
                : (loan.transactions || [])
                      .filter((t) => t.type === 'payment')
                      .reduce(
                          (acc, t) =>
                              acc +
                              (t.toPrincipal != null
                                  ? Number(t.toPrincipal)
                                  : Number(t.amount) || 0),
                          0,
                      );
        const balance = totalLoaned - principalPaid;
        const pct =
            totalLoaned > 0
                ? Math.min(
                      100,
                      Math.round((principalPaid / totalLoaned) * 100),
                  )
                : 0;

        const sortedTx = (loan.transactions || [])
            .slice()
            .sort((a, b) => new Date(a.date) - new Date(b.date));

        return (
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(15, 23, 42, 0.85)',
                    zIndex: 9800,
                    overflowY: 'auto',
                    fontFamily: FONT,
                    animation: 'bnFadeIn 0.2s ease',
                }}
            >
                {/* Top bar with print/close */}
                <div
                    className="bn-no-print"
                    style={{
                        position: 'sticky',
                        top: 0,
                        background: C.navyDark,
                        padding: '14px 22px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        zIndex: 2,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => this.setState({ showPdf: false })}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: C.white,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 14,
                            fontFamily: FONT,
                        }}
                    >
                        <IconBack size={20} /> Close
                    </button>
                    <button
                        type="button"
                        onClick={() => window.print()}
                        style={{
                            background: C.primaryGreen,
                            color: C.white,
                            border: 'none',
                            borderRadius: 999,
                            padding: '10px 22px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: FONT,
                        }}
                    >
                        Print / Save PDF
                    </button>
                </div>

                {/* Printable page */}
                <div
                    id="bn-loan-pdf-printable"
                    style={{
                        background: C.white,
                        maxWidth: 820,
                        margin: '24px auto',
                        padding: 0,
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                        borderRadius: 8,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header block */}
                    <div
                        style={{
                            background:
                                'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
                            color: C.white,
                            padding: '28px 36px',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                opacity: 0.7,
                                textTransform: 'uppercase',
                                letterSpacing: 2,
                                fontWeight: 500,
                            }}
                        >
                            BeeNexa
                        </div>
                        <div
                            style={{
                                fontSize: 26,
                                fontWeight: 700,
                                marginTop: 4,
                            }}
                        >
                            Loan Report
                        </div>
                        <div
                            style={{
                                fontSize: 14,
                                marginTop: 6,
                                opacity: 0.85,
                            }}
                        >
                            {loan.name} ·{' '}
                            {new Date().toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            })}
                        </div>
                    </div>

                    {/* Three summary cards */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: 14,
                            padding: '24px 36px 0',
                        }}
                    >
                        {[
                            {
                                label: 'Total Loaned',
                                amount: totalLoaned,
                                bg: C.navyDark,
                                color: C.white,
                            },
                            {
                                label: 'Total Paid',
                                amount: principalPaid,
                                bg: '#E8F5E9',
                                color: C.primaryGreen,
                            },
                            {
                                label: 'Balance Due',
                                amount: balance,
                                bg: '#FFEBEE',
                                color: C.deleteRed,
                            },
                        ].map((card) => (
                            <div
                                key={card.label}
                                style={{
                                    background: card.bg,
                                    color: card.color,
                                    padding: '18px 20px',
                                    borderRadius: 12,
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 11,
                                        opacity: 0.85,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                        fontWeight: 600,
                                    }}
                                >
                                    {card.label}
                                </div>
                                <div
                                    style={{
                                        fontSize: 22,
                                        fontWeight: 700,
                                        marginTop: 6,
                                    }}
                                >
                                    ${fmtMoney(card.amount, true)}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Progress bar */}
                    <div style={{ padding: '24px 36px 0' }}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.muted,
                                marginBottom: 6,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                            }}
                        >
                            <span>Repayment Progress</span>
                            <span>{pct}%</span>
                        </div>
                        <div
                            style={{
                                width: '100%',
                                height: 12,
                                background: '#E5E7EB',
                                borderRadius: 999,
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    width: `${pct}%`,
                                    height: '100%',
                                    background: C.primaryGreen,
                                    transition: 'width 0.3s ease',
                                }}
                            />
                        </div>
                    </div>

                    {/* Transaction table */}
                    <div style={{ padding: '24px 36px 0' }}>
                        <table
                            style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: 13,
                            }}
                        >
                            <thead>
                                <tr style={{ background: '#F1F5F9' }}>
                                    {['Date', 'Type', 'Note', 'Amount'].map((h) => (
                                        <th
                                            key={h}
                                            style={{
                                                textAlign:
                                                    h === 'Amount' ? 'right' : 'left',
                                                padding: '12px 14px',
                                                fontWeight: 600,
                                                color: C.navyDark,
                                                fontSize: 11,
                                                textTransform: 'uppercase',
                                                letterSpacing: 1,
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedTx.map((t, idx) => {
                                    const isPayment = t.type === 'payment';
                                    const hasSplit =
                                        isPayment &&
                                        t.toInterest != null &&
                                        Number(t.toInterest) > 0 &&
                                        t.toPrincipal != null;
                                    const headline = hasSplit
                                        ? Number(t.toPrincipal)
                                        : Number(t.amount);
                                    return (
                                        <tr
                                            key={t.entryId || idx}
                                            style={{
                                                background:
                                                    idx % 2 === 0
                                                        ? C.white
                                                        : '#FAFAFA',
                                            }}
                                        >
                                            <td
                                                style={{
                                                    padding: '12px 14px',
                                                    color: C.navyDark,
                                                }}
                                            >
                                                {fmtDate(t.date)}
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 14px',
                                                    textTransform: 'capitalize',
                                                    color: C.navyDark,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {t.type}
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 14px',
                                                    color: C.muted,
                                                }}
                                            >
                                                <div>{t.note || '—'}</div>
                                                {hasSplit && (
                                                    <div
                                                        style={{
                                                            fontSize: 11,
                                                            fontStyle: 'italic',
                                                            color: '#94A3B8',
                                                            marginTop: 2,
                                                        }}
                                                    >
                                                        Total payment: $
                                                        {fmtMoney(t.amount, true)}
                                                        {' (interest $'}
                                                        {fmtMoney(
                                                            t.toInterest,
                                                            true,
                                                        )}
                                                        {')'}
                                                    </div>
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    padding: '12px 14px',
                                                    textAlign: 'right',
                                                    fontWeight: 700,
                                                    color: isPayment
                                                        ? C.primaryGreen
                                                        : C.deleteRed,
                                                }}
                                            >
                                                {isPayment ? '−' : '+'} $
                                                {fmtMoney(headline, true)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Outstanding balance */}
                    <div
                        style={{
                            padding: '24px 36px',
                            textAlign: 'right',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                fontWeight: 600,
                            }}
                        >
                            Outstanding Balance
                        </div>
                        <div
                            style={{
                                fontSize: 30,
                                fontWeight: 700,
                                color: C.deleteRed,
                                marginTop: 4,
                            }}
                        >
                            ${fmtMoney(balance, true)}
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            borderTop: `1px solid ${C.border}`,
                            padding: '14px 36px',
                            fontSize: 11,
                            color: C.muted,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <span>Auto-generated by BeeNexa. Amounts in USD.</span>
                        <span>Page 1 of 1</span>
                    </div>
                </div>
            </div>
        );
    }

    // =================================================================
    //  RENDER — confirm dialog
    // =================================================================
    renderConfirmDialog() {
        const { confirm } = this.state;
        if (!confirm) return null;

        const accent = confirm.danger ? C.deleteRed : C.primaryGreen;

        return (
            <div
                onClick={this.closeConfirm}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(15, 23, 42, 0.55)',
                    zIndex: 9700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 20,
                    animation: 'bnFadeIn 0.18s ease',
                    fontFamily: FONT,
                }}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: C.white,
                        borderRadius: 20,
                        width: '100%',
                        maxWidth: 400,
                        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)',
                        animation: 'bnPopIn 0.22s ease',
                        overflow: 'hidden',
                    }}
                >
                    {/* Icon header */}
                    <div
                        style={{
                            padding: '28px 24px 0',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                background: confirm.danger
                                    ? '#FEE2E2'
                                    : '#E8F5E9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: accent,
                            }}
                        >
                            <IconTrash size={28} color={accent} />
                        </div>
                    </div>

                    {/* Title + message */}
                    <div style={{ padding: '20px 28px 24px', textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: 19,
                                fontWeight: 700,
                                color: C.navyDark,
                                marginBottom: 8,
                            }}
                        >
                            {confirm.title}
                        </div>
                        <div
                            style={{
                                fontSize: 14,
                                color: C.muted,
                                lineHeight: 1.55,
                            }}
                        >
                            {confirm.message}
                        </div>
                    </div>

                    {/* Buttons */}
                    <div
                        style={{
                            display: 'flex',
                            gap: 10,
                            padding: '0 24px 24px',
                        }}
                    >
                        <button
                            type="button"
                            onClick={this.closeConfirm}
                            style={{
                                flex: 1,
                                padding: '13px 16px',
                                background: '#F1F5F9',
                                color: C.navyDark,
                                border: 'none',
                                borderRadius: 12,
                                fontFamily: FONT,
                                fontSize: 15,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirm.onConfirm}
                            style={{
                                flex: 1,
                                padding: '13px 16px',
                                background: accent,
                                color: C.white,
                                border: 'none',
                                borderRadius: 12,
                                fontFamily: FONT,
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: 'pointer',
                                boxShadow: `0 8px 20px ${accent}40`,
                            }}
                        >
                            {confirm.confirmLabel || 'Confirm'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // =================================================================
    //  RENDER — root
    // =================================================================
    render() {
        return (
            <div
                className="bn-loans-root"
                style={{
                    background: C.bg,
                    minHeight: '100vh',
                    position: 'relative',
                }}
            >
                {this.renderHeader()}
                {this.state.view === 'list'
                    ? this.renderListView()
                    : this.renderDetailView()}

                {/* Floating Action Button — only on list view */}
                {this.state.view === 'list' && (
                    <button
                        type="button"
                        onClick={() => this.openSheet('new')}
                        className="bn-no-print"
                        style={{
                            position: 'fixed',
                            bottom: 32,
                            right: 32,
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: C.primaryGreen,
                            color: C.white,
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0 12px 32px rgba(45, 90, 61, 0.45)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 200,
                        }}
                    >
                        <IconPlus size={28} />
                    </button>
                )}

                {this.renderSheets()}
                {this.renderPdfOverlay()}
                {this.renderConfirmDialog()}
                <Snackbar
                    message={this.state.snackbar.message}
                    severity={this.state.snackbar.severity}
                    onClose={this.closeSnackbar}
                />
            </div>
        );
    }
}

// ---------------------------------------------------------------------------
//  Container
// ---------------------------------------------------------------------------

const mapStateToProps = (state) => ({
    userInfo: userInfoSelector(state),
    borrowers: borrowersSelector(state),
    totalOutstanding: totalOutstandingSelector(state),
    activeLoans: activeLoansSelector(state),
});

const actionCreators = {
    listCustomerLoan,
    createBorrower,
    editBorrower,
    addTransaction,
    editTransaction,
    deleteTransaction,
    deleteBorrower,
};

export default withShipment({ mapStateToProps, actionCreators }, LoanReport);
